// ==============================
// User guide content
// ==============================
// Generated from the customer user guide (docs/AquaticsHub_HuongDanSuDung_v1.0.7.docx,
// itself based on docs/HUONG_DAN_SU_DUNG.md). Screenshots live in
// public/user-guide/<img>.jpg. Inline text supports **bold**, *italic* and `code` markers.

export type UserGuideFigure = {
  img: string;
  caption: string;
  label: string;
  /** Intrinsic pixel size of public/user-guide/<img>.jpg. */
  width: number;
  height: number;
};

export type UserGuideStep = {
  text: string;
  sub?: string[];
  figure?: UserGuideFigure;
  /** Small dialog screenshot — rendered narrower than a full screen. */
  compact?: boolean;
};

export type UserGuideBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'subheading'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'note'; kind: 'note' | 'tip' | 'warn'; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | ({ type: 'figure' } & UserGuideFigure)
  | { type: 'steps'; items: UserGuideStep[] };

export type UserGuideSection = {
  id: string;
  title: string;
  blocks: UserGuideBlock[];
};

export type UserGuideAudience =
  'general' | 'admin' | 'coach' | 'public' | 'reference';

export type UserGuideChapter = {
  id: string;
  no: number;
  audience: UserGuideAudience;
  title: string;
  blocks: UserGuideBlock[];
  sections: UserGuideSection[];
};

export const USER_GUIDE_VERSION = '1.0.7';

export const userGuideChapters: UserGuideChapter[] = [
  {
    id: 'c1',
    no: 1,
    audience: 'general',
    title: 'Giới thiệu hệ thống',
    blocks: [
      {
        type: 'paragraph',
        text: '**Aquatics Hub** là phần mềm quản lý vận hành trung tâm bơi lội, chạy trên trình duyệt web (máy tính, máy tính bảng, điện thoại) và không cần cài đặt. Tài liệu này hướng dẫn chi tiết từng màn hình và từng thao tác, theo dạng **Bước 1, Bước 2, …** kèm hình chụp màn hình tại mỗi bước để người dùng dễ theo dõi.',
      },
    ],
    sections: [
      {
        id: 'c1-s1',
        title: '1.1 Các nhóm chức năng chính',
        blocks: [
          {
            type: 'table',
            headers: ['Nhóm chức năng', 'Hệ thống hỗ trợ'],
            rows: [
              [
                'Quản lý khách hàng',
                'Lưu hồ sơ khách cá nhân, trẻ em, doanh nghiệp; phát hiện và gộp hồ sơ trùng; hoàn tác gộp',
              ],
              [
                'Bán gói dịch vụ',
                'Bán khóa học (COURSE) và vé (TICKET); tính giá, giảm giá, VAT; xuất hóa đơn điện tử M-Invoice; in/tải phiếu xác nhận đăng ký (PDF)',
              ],
              [
                'Học thử',
                'Ghi nhận lịch học thử và kết quả: Ghi danh / Đang chờ / Không đến / Đang cân nhắc',
              ],
              [
                'Lớp học',
                'Tạo lớp lẻ hoặc hàng loạt, lịch tuần/tháng, sao chép lịch sang tuần sau, quản lý sĩ số, gán huấn luyện viên và học viên',
              ],
              [
                'Điểm danh',
                'Điểm danh học viên theo gói, tự trừ số buổi, hoàn tác khi điểm danh nhầm',
              ],
              [
                'Đánh giá level',
                'Huấn luyện viên đánh dấu hoàn thành từng kỹ năng (stage); hệ thống tự cập nhật level',
              ],
              [
                'Xếp lớp',
                'Danh sách học viên đã mua khóa học nhưng chưa có lớp, gợi ý lớp phù hợp còn chỗ',
              ],
              [
                'Tra cứu phụ huynh',
                'Trang công khai để phụ huynh xem gói, lớp, số buổi còn lại, lịch học của con',
              ],
              [
                'Khảo sát khách hàng',
                'Biểu mẫu online qua mã QR; chuyển phản hồi thành lịch học thử hoặc đăng ký',
              ],
              [
                'Báo cáo',
                'Doanh thu, khách hàng mới, tỷ lệ chuyển đổi học thử; xuất Excel',
              ],
              [
                'Quản trị',
                'Danh mục dịch vụ, level khóa học, nhập/xuất Excel hàng loạt, nhật ký thao tác, tài khoản nhân viên',
              ],
            ],
          },
        ],
      },
      {
        id: 'c1-s2',
        title: '1.2 Các khái niệm cơ bản',
        blocks: [
          {
            type: 'bullets',
            items: [
              '**Danh mục dịch vụ có 3 cấp:** Sản phẩm (vd. `COURSE`, `TICKET`) → Loại dịch vụ / Chương trình (vd. SOF, Flying Fish, Adult Courses) → Gói (gói cụ thể có giá, thời hạn, số buổi).',
              '**Đăng ký (Registration):** mỗi lần khách mua một gói. Một khách có thể có nhiều đăng ký.',
              '**Loại lượt của gói:** *count* — tính theo số buổi, mỗi lần điểm danh trừ 1 buổi; *unlimited* — không giới hạn số buổi trong thời hạn gói.',
              '**Level / Stage:** khóa học chia thành nhiều level (cấp độ); mỗi level gồm nhiều stage (kỹ năng cần đạt).',
              '**Khách vãng lai (Ẩn danh):** khách mua lẻ không cần lưu hồ sơ, dùng mã khách đặc biệt **999999**.',
            ],
          },
        ],
      },
      {
        id: 'c1-s3',
        title: '1.3 Yêu cầu sử dụng',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Trình duyệt: Google Chrome (khuyến nghị), Microsoft Edge, Safari hoặc Firefox phiên bản mới.',
              'Độ phân giải màn hình khuyến nghị cho nhân viên: từ 1366 × 768 trở lên. Trang dành cho phụ huynh và biểu mẫu khảo sát được tối ưu cho điện thoại.',
              'Kết nối Internet ổn định. Không cần cài đặt phần mềm.',
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Hình minh họa trong tài liệu sử dụng dữ liệu mẫu; tên, số điện thoại, email là dữ liệu giả lập. Giao diện được chụp ở chế độ ngôn ngữ Tiếng Việt; một số hộp thoại hiển thị nhãn tiếng Anh đúng như trên hệ thống.',
          },
        ],
      },
    ],
  },
  {
    id: 'c2',
    no: 2,
    audience: 'general',
    title: 'Vai trò người dùng và phân quyền',
    blocks: [
      {
        type: 'table',
        headers: ['Vai trò', 'Người sử dụng', 'Trang mở ra sau khi đăng nhập'],
        rows: [
          [
            '**Quản trị viên (Admin)**',
            'Nhân viên quầy, quản lý trung tâm',
            'Trang chủ',
          ],
          [
            '**Huấn luyện viên (Coach)**',
            'Huấn luyện viên đứng lớp',
            'Đánh giá (Assessment)',
          ],
          [
            '**Khách hàng / Phụ huynh**',
            'Không cần tài khoản',
            'Trang khảo sát, trang tra cứu',
          ],
        ],
      },
      {
        type: 'note',
        kind: 'note',
        text: 'Tài khoản nhân viên do Quản trị viên tạo tại **Cài đặt → Quản lý tài khoản**. Hệ thống không cho phép tự đăng ký tài khoản.',
      },
    ],
    sections: [
      {
        id: 'c2-s1',
        title: '2.1 Bảng quyền thao tác',
        blocks: [
          {
            type: 'table',
            headers: ['Thao tác', 'Quản trị viên', 'Huấn luyện viên'],
            rows: [
              ['Tạo / sửa / xóa khách hàng', '✔', '✘'],
              [
                'Bán gói, sửa đăng ký, xuất hóa đơn, in phiếu xác nhận',
                '✔',
                '✘',
              ],
              ['Tạo / sửa / xóa lớp, gán huấn luyện viên', '✔', '✘'],
              [
                'Thêm học viên vào lớp',
                '✔ (mọi lớp)',
                '✔ (lớp được giao, qua Học viên chờ xếp lớp)',
              ],
              [
                'Điểm danh / hoàn tác điểm danh',
                '✔',
                '✔ (học viên trong lớp được giao)',
              ],
              [
                'Đánh giá stage, đặt level',
                '✔ (tại Dữ liệu gốc)',
                '✔ (học viên trong lớp được giao)',
              ],
              [
                'Xem danh bạ học viên (Dữ liệu gốc)',
                '✔ (xem và sửa)',
                '✔ (chỉ xem)',
              ],
              ['Lưu trữ / bỏ lưu trữ học viên', '✔', '✘'],
              ['Báo cáo, Cài đặt, Nhật ký kiểm toán', '✔', '✘'],
            ],
          },
        ],
      },
      {
        id: 'c2-s2',
        title: '2.2 Menu theo vai trò',
        blocks: [
          {
            type: 'paragraph',
            text: '**Menu Quản trị viên:** Trang chủ · Khách hàng · Huấn luyện viên · Đặt lịch học thử · Đăng ký · Đánh giá · Lớp học · Dữ liệu gốc · Học viên chờ xếp lớp · Báo cáo · Phản hồi khảo sát · Cài đặt (Cài đặt dịch vụ, Cài đặt khóa học, Cài đặt biểu mẫu, Nhập dữ liệu, Nhật ký kiểm toán, Quản lý tài khoản, Cài đặt tra cứu phụ huynh, Tra cứu phụ huynh quản trị).',
          },
          {
            type: 'paragraph',
            text: '**Menu Huấn luyện viên:** Assessment · Class Management · Master Data · Pending Enrollment.',
          },
        ],
      },
    ],
  },
  {
    id: 'c3',
    no: 3,
    audience: 'general',
    title: 'Bắt đầu sử dụng',
    blocks: [],
    sections: [
      {
        id: 'c3-s1',
        title: '3.1 Đăng nhập',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Mở địa chỉ trang web của hệ thống do trung tâm cung cấp. Màn hình **Đăng nhập** hiển thị.',
                figure: {
                  img: 'a03_signin',
                  caption: 'Màn hình Đăng nhập',
                  label: 'Hình 3.1',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Nhập **Email** và **Mật khẩu** (8–16 ký tự) của tài khoản được cấp.',
              },
              {
                text: 'Nhấn nút **Đăng nhập**.',
              },
              {
                text: 'Hệ thống tự chuyển đến trang chính theo vai trò: Quản trị viên → **Trang chủ**; Huấn luyện viên → **Assessment**.',
              },
            ],
          },
          {
            type: 'table',
            headers: ['Thông báo', 'Nguyên nhân', 'Cách xử lý'],
            rows: [
              [
                'User does not exist',
                'Email chưa có tài khoản hoặc tài khoản đã bị vô hiệu hóa',
                'Kiểm tra lại email; liên hệ Quản trị viên',
              ],
              [
                'Sai mật khẩu',
                'Mật khẩu không đúng',
                'Nhập lại; nhờ Quản trị viên đặt lại mật khẩu nếu quên',
              ],
            ],
          },
          {
            type: 'note',
            kind: 'tip',
            text: 'Sau khi đăng nhập, phiên làm việc được tự động gia hạn trong ngày. Nếu phiên hết hạn, hệ thống đưa bạn về trang Đăng nhập — chỉ cần đăng nhập lại.',
          },
        ],
      },
      {
        id: 'c3-s2',
        title: '3.2 Bố cục giao diện',
        blocks: [
          {
            type: 'paragraph',
            text: 'Sau khi đăng nhập, màn hình gồm 3 khu vực chính:',
          },
          {
            img: 'a03_layout',
            caption:
              'Bố cục chung: thanh trên cùng, menu bên trái và vùng nội dung',
            label: 'Hình 3.2',
            type: 'figure',
            width: 1600,
            height: 900,
          },
          {
            type: 'table',
            headers: ['Khu vực', 'Chức năng'],
            rows: [
              [
                'Thanh trên cùng',
                'Logo trung tâm; nút đổi ngôn ngữ (VI/EN); nút chế độ sáng/tối; tên người dùng và menu Đăng xuất',
              ],
              [
                'Menu bên trái',
                'Danh sách màn hình theo vai trò; nhóm Cài đặt có thể mở rộng/thu gọn; nút « để thu nhỏ menu',
              ],
              [
                'Vùng nội dung',
                'Tiêu đề màn hình, mô tả ngắn, bộ lọc, bảng dữ liệu và các nút thao tác',
              ],
            ],
          },
        ],
      },
      {
        id: 'c3-s3',
        title: '3.3 Đổi ngôn ngữ giao diện',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng quả địa cầu (**VI**/**EN**) trên thanh trên cùng.',
                figure: {
                  img: 'a03_lang_menu',
                  caption: 'Chọn ngôn ngữ hiển thị',
                  label: 'Hình 3.3',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Chọn **VI** (Tiếng Việt) hoặc **EN** (English). Lựa chọn được ghi nhớ cho các lần sử dụng sau trên cùng trình duyệt.',
              },
            ],
          },
        ],
      },
      {
        id: 'c3-s4',
        title: '3.4 Đăng xuất',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn vào **tên người dùng** ở góc phải thanh trên cùng.',
                figure: {
                  img: 'a03_user_menu',
                  caption: 'Menu người dùng',
                  label: 'Hình 3.4',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Chọn **Đăng xuất**. Nên đăng xuất khi sử dụng máy tính dùng chung.',
              },
            ],
          },
        ],
      },
      {
        id: 'c3-s5',
        title: '3.5 Thao tác chung trên bảng dữ liệu',
        blocks: [
          {
            type: 'table',
            headers: ['Thành phần', 'Cách sử dụng'],
            rows: [
              [
                'Ô tìm kiếm',
                'Nhập từ khóa rồi nhấn Enter. Thường tìm được theo mã, tên, số điện thoại, email',
              ],
              [
                'Bộ lọc (danh sách thả xuống)',
                'Chọn giá trị để thu hẹp dữ liệu: trạng thái, chương trình, gói…',
              ],
              [
                'Xóa bộ lọc',
                'Xuất hiện khi đang lọc; nhấn để trở về toàn bộ dữ liệu',
              ],
              [
                'Biểu tượng tải xuống ⬇',
                'Tải dữ liệu đang xem về file Excel (.xlsx)',
              ],
              [
                'Phân trang',
                'Chọn số dòng mỗi trang (Rows per page) và chuyển trang ở cuối bảng',
              ],
              ['Đồng bộ lần cuối', 'Thời điểm dữ liệu được làm mới gần nhất'],
              [
                'Biểu tượng trên dòng',
                'Xem, Sửa, Xóa… — rê chuột lên biểu tượng để xem chú thích',
              ],
            ],
          },
          {
            type: 'bullets',
            items: [
              'Các bảng Khách hàng, Huấn luyện viên, Học thử, Đăng ký, Đánh giá, Lớp học **tự làm mới** liên tục — dữ liệu đồng nghiệp nhập sẽ tự hiện ra.',
              'Nhấn vào **email** trong bảng để mở Gmail soạn thư; nhấn vào **số điện thoại** để mở Zalo.',
              'Dòng của khách vãng lai được tô màu và gắn nhãn **Ẩn danh**.',
              'Mọi thao tác thêm/sửa/xóa đều được ghi lại trong **Nhật ký kiểm toán**.',
              'Thao tác quan trọng (xóa, xuất hóa đơn, điểm danh…) luôn có hộp thoại xác nhận — đọc kỹ trước khi nhấn **Xác nhận / Confirm**.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c4',
    no: 4,
    audience: 'admin',
    title: 'Trang chủ',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Trang chủ. **Mục đích:** điểm bắt đầu của nhân viên quầy — tìm khách nhanh và khởi động các nghiệp vụ thường dùng: tạo khách, bán gói, đặt lịch học thử.',
      },
      {
        img: 'b05_home',
        caption: 'Trang chủ: ô Tìm khách hàng và 4 thẻ thao tác nhanh',
        label: 'Hình 4.1',
        type: 'figure',
        width: 1600,
        height: 900,
      },
    ],
    sections: [
      {
        id: 'c4-s1',
        title: '4.1 Tìm khách hàng',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhập **ít nhất 2 ký tự** vào ô **Tìm khách hàng**: số điện thoại, tên, email hoặc mã khách.',
                figure: {
                  img: 'b05_home_search',
                  caption:
                    'Kết quả tìm kiếm khách hàng (tối đa 8 kết quả, nhãn "Đã tồn tại")',
                  label: 'Hình 4.2',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Chọn khách trong danh sách kết quả để mở ngay trình **ghi danh gói dịch vụ** cho khách đó (xem Chương 5).',
              },
              {
                text: 'Nếu **không tìm thấy**, hệ thống gợi ý tạo mới: **Khách hàng mới** hoặc **Tổ chức mới**.',
                figure: {
                  img: 'b05_home_notfound',
                  caption: 'Không tìm thấy khách — gợi ý tạo mới',
                  label: 'Hình 4.3',
                  width: 1600,
                  height: 1091,
                },
              },
            ],
          },
          {
            type: 'note',
            kind: 'tip',
            text: 'Luôn tìm khách trước khi tạo mới để tránh trùng hồ sơ.',
          },
        ],
      },
      {
        id: 'c4-s2',
        title: '4.2 Tạo khách hàng cá nhân mới (Người dùng mới)',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn thẻ **Người dùng mới**. Biểu mẫu tạo khách hiển thị.',
                figure: {
                  img: 'b05_new_user',
                  caption: 'Biểu mẫu tạo khách hàng cá nhân',
                  label: 'Hình 4.4',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhập **Last Name** (Họ, bắt buộc), **First Name** (Tên, bắt buộc), Preferred Name (tên gọi), Gender (giới tính), Date of Birth (ngày sinh).',
              },
              {
                text: 'Nhấn **ADD**. Hệ thống tạo hồ sơ — tự phân loại **Child** nếu dưới 16 tuổi, ngược lại là **Adult** — và mở ngay trình ghi danh gói (Chương 5).',
              },
            ],
          },
        ],
      },
      {
        id: 'c4-s3',
        title: '4.3 Tạo khách hàng doanh nghiệp (Tổ chức mới)',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn thẻ **Tổ chức mới**.',
                figure: {
                  img: 'b05_new_org',
                  caption: 'Biểu mẫu tạo khách hàng doanh nghiệp / tổ chức',
                  label: 'Hình 4.5',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhập **Last Name**, **First Name** (người liên hệ) và **Business Name** (tên doanh nghiệp) — đều bắt buộc.',
              },
              {
                text: 'Nhấn **ADD**. Hệ thống tạo khách loại **Business** và mở trình ghi danh gói.',
              },
            ],
          },
        ],
      },
      {
        id: 'c4-s4',
        title: '4.4 Ghi danh cho khách hàng hiện có',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn thẻ **Khách hàng / tổ chức hiện có**.',
                figure: {
                  img: 'b05_existing',
                  caption: 'Chọn khách hàng hiện có',
                  label: 'Hình 4.6',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Tại ô **User (Phone - ID - Name)**, gõ số điện thoại, mã hoặc tên khách (tìm được cả khi gõ không dấu) và chọn khách.',
              },
              {
                text: 'Nhấn **ENROLL** để mở trình ghi danh gói (Chương 5).',
              },
            ],
          },
        ],
      },
      {
        id: 'c4-s5',
        title: '4.5 Đặt lịch học thử nhanh',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn thẻ **Đặt lịch học thử**.',
                figure: {
                  img: 'b05_trial',
                  caption: 'Biểu mẫu đặt lịch học thử',
                  label: 'Hình 4.7',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhập họ tên (bắt buộc), Email, **Phone** (bắt buộc), Parent Name, **Date of Birth** (bắt buộc), **Trial Date** (ngày học thử, bắt buộc), **Program** (chương trình, bắt buộc), Note.',
              },
              {
                text: 'Nhấn **ADD**. Lịch học thử được tạo với kết quả **Đang chờ** (xem Chương 8).',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c5',
    no: 5,
    audience: 'admin',
    title: 'Ghi danh gói dịch vụ cho khách hàng',
    blocks: [
      {
        type: 'paragraph',
        text: 'Trình ghi danh được mở từ các thẻ trên **Trang chủ**, từ nút **GHI DANH** trên màn hình **Đăng ký**, hoặc từ nút **THÊM KHÁCH HÀNG / THÊM TỔ CHỨC** trên màn hình **Khách hàng**. Quy trình gồm 3 phần: chọn gói → bổ sung thông tin → xác nhận.',
      },
      {
        type: 'steps',
        items: [
          {
            text: 'Trên màn hình **Đăng ký**, nhấn **GHI DANH**. Hộp chọn khách hiển thị.',
            figure: {
              img: 'b06_enroll_select_user',
              caption: 'Chọn khách hàng cần ghi danh',
              label: 'Hình 5.1',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Tại ô **User (Phone - ID - Name)**, nhập mã/số điện thoại/tên và chọn khách, sau đó nhấn **ENROLL**.',
            figure: {
              img: 'b06_enroll_user_chosen',
              caption: 'Đã chọn khách hàng',
              label: 'Hình 5.2',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Hộp **Service registration form** hiển thị danh sách sản phẩm. Nhấn vào sản phẩm (vd. **COURSE**).',
            figure: {
              img: 'b06_step1_packages',
              caption: 'Bước chọn sản phẩm',
              label: 'Hình 5.3',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Nhấn vào loại dịch vụ (vd. **SOF**) để xem các gói, rồi nhấn vào **gói** cần bán.',
            figure: {
              img: 'b06_step1_tree',
              caption: 'Chọn loại dịch vụ và gói',
              label: 'Hình 5.4',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Hộp **Add / Update additional Information** hiển thị. Kiểm tra và bổ sung thông tin khách, sau đó nhấn **CONFIRM**.',
            sub: [
              '**Phone** (bắt buộc) — nếu khách không cung cấp, tick **Customer not providing phone number**.',
              '**Address** (bắt buộc) — nếu khách không cung cấp, tick **Customer not providing address**.',
              '**Email**, **Identity Number** (CCCD): không bắt buộc.',
              '**Tax Code** (mã số thuế): bắt buộc với khách doanh nghiệp — dùng để xuất hóa đơn.',
              '**Parent Name**: hiện với gói dành cho trẻ em. **Walk-in Customer Name**: chỉ hiện khi bán cho khách vãng lai 999999.',
            ],
            figure: {
              img: 'b06_step2_info',
              caption: 'Bổ sung thông tin khách hàng',
              label: 'Hình 5.5',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Hộp **Confirm Registration** hiển thị tên khách, gói, giá. Điền các thông tin xác nhận rồi nhấn **CONFIRM** để hoàn tất.',
            sub: [
              '**Course Level**: level bắt đầu của học viên (chỉ với khóa học có level).',
              '**Select service start date**: ngày bắt đầu gói. Ngày kết thúc được tự tính = ngày bắt đầu + thời hạn gói.',
              '**Payment Method**: Cash (tiền mặt) / Bank Transfer (chuyển khoản) / Deposit (đặt cọc).',
              '**Discount Amount**: số tiền giảm giá; **Quantity / Child Quantity**: số lượng (gói nhóm); **Note**: ghi chú nội bộ.',
            ],
            figure: {
              img: 'b06_step3_confirm',
              caption: 'Xác nhận đăng ký',
              label: 'Hình 5.6',
              width: 1600,
              height: 1091,
            },
          },
        ],
      },
      {
        type: 'note',
        kind: 'note',
        text: 'Thông tin bổ sung ở bước "Add / Update additional Information" được lưu vào hồ sơ khách để lần sau không phải nhập lại (trừ khách vãng lai).',
      },
      {
        type: 'note',
        kind: 'warn',
        text: 'Ở bước này **chưa xuất hóa đơn**. Hóa đơn được xuất tại màn hình **Đăng ký** (mục 9.3). Với khóa học, sau khi bán gói cần **xếp lớp** cho học viên tại **Học viên chờ xếp lớp** (Chương 13).',
      },
      {
        type: 'paragraph',
        text: 'Giá, VAT, tên trên hóa đơn và mẫu phiếu xác nhận được **chốt tại thời điểm bán** — thay đổi danh mục sau này không ảnh hưởng đến đăng ký cũ. Nếu học viên đang bị lưu trữ mua khóa học mới còn hạn, hệ thống tự bỏ lưu trữ.',
      },
    ],
    sections: [],
  },
  {
    id: 'c6',
    no: 6,
    audience: 'admin',
    title: 'Khách hàng',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Khách hàng. **Mục đích:** quản lý toàn bộ hồ sơ khách hàng; phát hiện và gộp hồ sơ trùng. Màn hình có 2 chế độ: **CÁ NHÂN** và **NHÓM**.',
      },
    ],
    sections: [
      {
        id: 'c6-s1',
        title: '6.1 Danh sách khách hàng (chế độ Cá nhân)',
        blocks: [
          {
            img: 'b07_user_list',
            caption: 'Màn hình Quản lý khách hàng',
            label: 'Hình 6.1',
            type: 'figure',
            width: 1600,
            height: 1091,
          },
          {
            type: 'bullets',
            items: [
              'Ô tìm kiếm theo ID, tên, số điện thoại, email.',
              '**Cùng số điện thoại / Cùng email**: chỉ hiện các hồ sơ đang bị trùng.',
              '**Loại khách hàng**: Trẻ em, Người lớn, Doanh nghiệp. Biểu tượng ⬇ để tải danh sách Excel.',
              'Biểu tượng trên mỗi dòng: 👁 Xem hồ sơ · ✏️ Chỉnh sửa · 🗑 Xóa khách hàng.',
            ],
          },
        ],
      },
      {
        id: 'c6-s2',
        title: '6.2 Xem và chỉnh sửa hồ sơ khách',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng 👁 **Xem hồ sơ** trên dòng khách. Hộp **Client Profile** mở ở tab **PROFILE**.',
                figure: {
                  img: 'b07_user_profile',
                  caption: 'Hồ sơ khách hàng — tab Profile',
                  label: 'Hình 6.2',
                  width: 1350,
                  height: 1248,
                },
              },
              {
                text: 'Chuyển sang tab **SERVICES** để xem level hiện tại và dòng thời gian các gói khách đã mua. Với gói tính theo buổi, có thể sửa **Remaining sessions** (số buổi còn lại).',
                figure: {
                  img: 'b07_user_services',
                  caption: 'Hồ sơ khách hàng — tab Services',
                  label: 'Hình 6.3',
                  width: 1350,
                  height: 912,
                },
              },
              {
                text: 'Nhấn **EDIT** để chuyển sang chế độ sửa, cập nhật thông tin rồi lưu; hoặc nhấn **CLOSE** để đóng.',
              },
            ],
          },
        ],
      },
      {
        id: 'c6-s3',
        title: '6.3 Tìm hồ sơ trùng',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tick **Cùng số điện thoại** (hoặc **Cùng email**) để lọc các hồ sơ đang bị trùng.',
                figure: {
                  img: 'b07_user_samephone',
                  caption: 'Lọc khách hàng trùng số điện thoại',
                  label: 'Hình 6.4',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Đối chiếu thông tin; nếu là cùng một người, dùng chế độ **NHÓM** để gộp hồ sơ (mục 6.4).',
              },
            ],
          },
        ],
      },
      {
        id: 'c6-s4',
        title: '6.4 Gộp hồ sơ trùng (chế độ Nhóm)',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **NHÓM** ở đầu trang. Chọn tiêu chí nhóm (Group by): Phone, Email, Phone and Email, Business Name hoặc Parent. Chỉ các nhóm có từ 2 hồ sơ trở lên được hiển thị.',
                figure: {
                  img: 'b07_user_group',
                  caption: 'Chế độ Nhóm — tìm và gộp hồ sơ trùng',
                  label: 'Hình 6.5',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Mở một nhóm; xem cây gia đình (Tree) hoặc dịch vụ (Services) để đối chiếu.',
              },
              {
                text: 'Tick chọn **từ 2 hồ sơ trở lên** cần gộp → nhấn **Merge**.',
              },
              {
                text: 'Chọn **hồ sơ giữ lại** (hồ sơ chính) → xác nhận.',
              },
            ],
          },
          {
            type: 'paragraph',
            text: 'Sau khi gộp: toàn bộ đăng ký, lịch học thử, phản hồi khảo sát của hồ sơ phụ được chuyển sang hồ sơ chính; các trường còn trống được bổ sung; hồ sơ phụ được lưu trữ.',
          },
          {
            type: 'subheading',
            text: 'Hoàn tác gộp hồ sơ',
          },
          {
            type: 'steps',
            items: [
              {
                text: 'Mở mục **Lịch sử gộp hồ sơ** (Merge history) — liệt kê thời điểm, hồ sơ giữ lại, các hồ sơ đã gộp, số mục đã chuyển, người thực hiện.',
              },
              {
                text: 'Nhấn **Hoàn tác** trên dòng cần hoàn tác → xác nhận. Các hồ sơ đã gộp được khôi phục cùng dịch vụ của họ.',
              },
            ],
          },
        ],
      },
      {
        id: 'c6-s5',
        title: '6.5 Thêm khách hàng mới',
        blocks: [
          {
            type: 'paragraph',
            text: 'Nhấn **THÊM KHÁCH HÀNG** hoặc **THÊM TỔ CHỨC** — thao tác giống thẻ trên Trang chủ (mục 4.2, 4.3).',
          },
        ],
      },
    ],
  },
  {
    id: 'c7',
    no: 7,
    audience: 'admin',
    title: 'Huấn luyện viên',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Huấn luyện viên. **Mục đích:** quản lý nhanh tài khoản đăng nhập của huấn luyện viên. Để quản lý cả tài khoản Quản trị viên, đặt lại mật khẩu hoặc vô hiệu hóa tài khoản, dùng **Cài đặt → Quản lý tài khoản** (Chương 21).',
      },
      {
        img: 'b08_coach_list',
        caption: 'Màn hình Quản lý huấn luyện viên',
        label: 'Hình 7.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c7-s1',
        title: '7.1 Thêm huấn luyện viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM HUẤN LUYỆN VIÊN**. Hộp **Create New Coach** hiển thị.',
                figure: {
                  img: 'b08_coach_add',
                  caption: 'Thêm huấn luyện viên',
                  label: 'Hình 7.2',
                  width: 900,
                  height: 489,
                },
                compact: true,
              },
              {
                text: 'Nhập **Coach Name**, **Email Address**, **Password** (8–16 ký tự).',
              },
              {
                text: 'Nhấn **CREATE**. Email đã tồn tại sẽ báo lỗi.',
              },
            ],
          },
        ],
      },
      {
        id: 'c7-s2',
        title: '7.2 Sửa, xóa huấn luyện viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn ✏️ **Sửa** trên dòng huấn luyện viên → sửa tên, email → lưu.',
                figure: {
                  img: 'b08_coach_edit',
                  caption: 'Sửa thông tin huấn luyện viên',
                  label: 'Hình 7.3',
                  width: 618,
                  height: 366,
                },
                compact: true,
              },
              {
                text: 'Nhấn 🗑 **Xóa** → xác nhận. Huấn luyện viên bị xóa sẽ không đăng nhập được nữa.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Để huấn luyện viên thấy lớp của mình, cần **gán huấn luyện viên vào lớp** tại **Lớp học → Quản lý lớp → ADD COACH** (mục 11.5).',
          },
        ],
      },
    ],
  },
  {
    id: 'c8',
    no: 8,
    audience: 'admin',
    title: 'Đặt lịch học thử',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Đặt lịch học thử. **Mục đích:** theo dõi các buổi học thử của khách tiềm năng và ghi nhận kết quả.',
      },
      {
        img: 'b09_trial_list',
        caption: 'Màn hình Quản lý đặt lịch học thử',
        label: 'Hình 8.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'bullets',
        items: [
          'Tìm theo tên, số điện thoại, email; lọc theo chương trình (**Tất cả dịch vụ**), kết quả (**Tất cả kết quả**), **ngày học thử**.',
          'Nhấn biểu tượng ⬇ **Tải danh sách học thử** để xuất Excel.',
        ],
      },
    ],
    sections: [
      {
        id: 'c8-s1',
        title: '8.1 Thêm lịch học thử',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM LỊCH HỌC THỬ MỚI**.',
                figure: {
                  img: 'b09_trial_add',
                  caption: 'Thêm lịch học thử mới',
                  label: 'Hình 8.2',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhập thông tin khách, chương trình và ngày học thử như mục 4.5 → nhấn **ADD**. Lịch mới có kết quả **Đang chờ**.',
              },
            ],
          },
        ],
      },
      {
        id: 'c8-s2',
        title: '8.2 Cập nhật kết quả học thử',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn ✏️ trên dòng lịch học thử. Hộp **Cập nhật thông tin học thử** hiển thị.',
                figure: {
                  img: 'b09_trial_edit',
                  caption: 'Cập nhật thông tin và kết quả học thử',
                  label: 'Hình 8.3',
                  width: 900,
                  height: 1185,
                },
                compact: true,
              },
              {
                text: 'Sửa thông tin nếu cần và chọn **Kết quả** phù hợp.',
              },
              {
                text: 'Nhấn **XÁC NHẬN**. Nếu chọn **Ghi danh (Enroll)**, hệ thống hỏi "Ghi danh người dùng này?" — xác nhận để tạo hồ sơ khách hàng mới từ thông tin học thử; lịch học thử bị khóa, không sửa được nữa.',
              },
            ],
          },
          {
            type: 'table',
            headers: ['Kết quả', 'Ý nghĩa'],
            rows: [
              ['Pending (Đang chờ)', 'Chưa học thử / chưa có kết quả'],
              ['Not come (Không đến)', 'Khách không đến buổi học thử'],
              ['Consider (Đang cân nhắc)', 'Khách đã học thử, đang cân nhắc'],
              ['Enroll (Ghi danh)', 'Khách đồng ý đăng ký học'],
            ],
          },
          {
            type: 'note',
            kind: 'warn',
            text: 'Sau khi **Enroll**, việc **bán gói chưa được thực hiện**. Hãy kiểm tra trùng hồ sơ tại **Khách hàng → NHÓM**, rồi bán gói tại **Đăng ký → GHI DANH** (Chương 5).',
          },
        ],
      },
    ],
  },
  {
    id: 'c9',
    no: 9,
    audience: 'admin',
    title: 'Quản lý đăng ký, hóa đơn và phiếu xác nhận',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Đăng ký. **Mục đích:** danh sách mọi giao dịch bán gói. Tại đây có thể sửa đăng ký, xuất hóa đơn điện tử, in/tải phiếu xác nhận, xóa đăng ký và xuất danh sách.',
      },
      {
        img: 'b10_reg_list',
        caption: 'Màn hình Quản lý đăng ký',
        label: 'Hình 9.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c9-s1',
        title: '9.1 Tìm kiếm và lọc',
        blocks: [
          {
            type: 'table',
            headers: ['Bộ lọc', 'Mô tả'],
            rows: [
              ['Tìm kiếm', 'Theo ID, tên, số điện thoại, email'],
              [
                'Sản phẩm → Loại dịch vụ → Gói',
                'Lọc theo danh mục, chọn từ cấp lớn đến cấp nhỏ',
              ],
              [
                'Trạng thái',
                'Đang hoạt động (còn hạn và còn buổi) / Không hoạt động',
              ],
              [
                'Khách hàng',
                'Chỉ ẩn danh (khách vãng lai) / Chỉ không ẩn danh',
              ],
              ['Phương thức thanh toán', 'Tiền mặt / Chuyển khoản / Đặt cọc'],
              ['Hóa đơn', 'Đã xuất hóa đơn / Chưa xuất hóa đơn'],
              ['Lọc theo tháng', 'Chọn tháng cần xem'],
            ],
          },
          {
            img: 'b10_reg_filter_invoice',
            caption: 'Ví dụ: lọc theo trạng thái hóa đơn',
            label: 'Hình 9.2',
            type: 'figure',
            width: 1600,
            height: 1091,
          },
          {
            type: 'paragraph',
            text: 'Các biểu tượng trên mỗi dòng (theo thứ tự): ✏️ **Chỉnh sửa đăng ký** · 🧾 **Tạo hóa đơn** · ✈ **Gửi xác nhận đăng ký** · ⬇ **Tải xác nhận đăng ký** · 🗑 **Xóa đăng ký**.',
          },
        ],
      },
      {
        id: 'c9-s2',
        title: '9.2 Sửa đăng ký',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn ✏️ **Chỉnh sửa đăng ký** (chỉ khả dụng khi đăng ký **chưa xuất hóa đơn**). Hộp **Update Registration Information** hiển thị.',
                figure: {
                  img: 'b10_reg_edit',
                  caption: 'Sửa thông tin đăng ký',
                  label: 'Hình 9.3',
                  width: 900,
                  height: 816,
                },
                compact: true,
              },
              {
                text: 'Sửa **Start Date**, **Duration (days)**, **Total price**, **Quantity**, **Discount Price**, phương thức thanh toán, **Note**.',
              },
              {
                text: 'Nhấn **CONFIRM**. Ngày kết thúc và giá được hệ thống tính lại tự động.',
              },
            ],
          },
        ],
      },
      {
        id: 'c9-s3',
        title: '9.3 Xuất hóa đơn điện tử',
        blocks: [
          {
            type: 'subheading',
            text: 'Xuất hóa đơn cho một đăng ký',
          },
          {
            type: 'steps',
            items: [
              {
                text: 'Lọc **Hóa đơn = Chưa xuất hóa đơn** để tìm các đăng ký chưa xuất.',
              },
              {
                text: 'Nhấn 🧾 **Tạo hóa đơn** trên dòng đăng ký. Đọc hộp xác nhận "Create invoice?".',
                figure: {
                  img: 'b10_reg_invoice_confirm',
                  caption: 'Xác nhận tạo hóa đơn',
                  label: 'Hình 9.4',
                  width: 900,
                  height: 222,
                },
                compact: true,
              },
              {
                text: 'Nhấn **Xác nhận**. Khi thành công, cột **Xuất hóa đơn** chuyển sang đã xuất; các nút Sửa/Xóa bị khóa.',
              },
            ],
          },
          {
            type: 'subheading',
            text: 'Gộp nhiều đăng ký vào một hóa đơn',
          },
          {
            type: 'steps',
            items: [
              {
                text: 'Tick chọn các đăng ký **của cùng một khách hàng**.',
                figure: {
                  img: 'b10_reg_multi_select',
                  caption: 'Chọn nhiều đăng ký của cùng khách để gộp hóa đơn',
                  label: 'Hình 9.5',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn **Tạo hóa đơn** → xác nhận "Create invoices for the selected registrations?".',
              },
              {
                text: 'Nhấn **Bỏ chọn** nếu muốn chọn lại.',
              },
            ],
          },
          {
            type: 'table',
            headers: ['Loại khách', 'Thông tin trên hóa đơn'],
            rows: [
              [
                'Khách vãng lai, hoặc khách cá nhân thiếu thông tin',
                '"Bán cho người tiêu dùng"',
              ],
              ['Khách doanh nghiệp', 'Tên doanh nghiệp + mã số thuế + địa chỉ'],
            ],
          },
          {
            type: 'note',
            kind: 'warn',
            text: 'Sau khi đã xuất hóa đơn, đăng ký **không thể sửa hoặc xóa** trên hệ thống; việc điều chỉnh hóa đơn thực hiện trên M-Invoice. Hãy kiểm tra kỹ trước khi xuất.',
          },
        ],
      },
      {
        id: 'c9-s4',
        title: '9.4 In / tải phiếu xác nhận đăng ký',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn ⬇ **Tải xác nhận đăng ký** trên dòng đăng ký (không áp dụng cho khách vãng lai). Hộp phiếu xác nhận hiển thị.',
                figure: {
                  img: 'b10_reg_confirmation',
                  caption:
                    'Phiếu xác nhận đăng ký: chỉnh nội dung và xem trước khổ A4',
                  label: 'Hình 9.6',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Kiểm tra/chỉnh các thông tin trên phiếu: **Full name**, **Member code**, **Phone**, **Service/package**, **Activation date**, **Expiry date**, điều khoản và thông tin hỗ trợ (tiếng Việt/tiếng Anh).',
              },
              {
                text: 'Chọn ngôn ngữ xem trước: **VIETNAMESE** hoặc **ENGLISH**.',
              },
              {
                text: 'Nhấn **PRINT** để in trực tiếp, hoặc **DOWNLOAD PDF** để tải file gửi khách.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Chức năng ✈ **Gửi xác nhận đăng ký** qua email đang được phát triển. Hiện tại hãy tải PDF và gửi cho khách.',
          },
        ],
      },
      {
        id: 'c9-s5',
        title: '9.5 Xóa đăng ký',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn 🗑 **Xóa đăng ký** (chỉ khi chưa xuất hóa đơn).',
              },
              {
                text: 'Đọc hộp "Delete registration?" và nhấn **Xác nhận**.',
              },
            ],
          },
        ],
      },
      {
        id: 'c9-s6',
        title: '9.6 Nhập đăng ký từ Excel',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **NHẬP DỮ LIỆU**. Tải file mẫu bằng **Download Excel Template**, điền dữ liệu, rồi chọn file và nhấn **Upload**.',
                figure: {
                  img: 'b10_reg_import',
                  caption: 'Nhập đăng ký từ file Excel',
                  label: 'Hình 9.7',
                  width: 1600,
                  height: 1091,
                },
              },
            ],
          },
          {
            type: 'note',
            kind: 'tip',
            text: 'Với lượng dữ liệu lớn, nên dùng **Cài đặt → Nhập dữ liệu** (Chương 19) — có xem trước, lịch sử và hoàn tác.',
          },
        ],
      },
    ],
  },
  {
    id: 'c10',
    no: 10,
    audience: 'admin',
    title: 'Đánh giá và điểm danh tại quầy',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Đánh giá. **Mục đích:** bảng điều khiển tại quầy để **điểm danh** theo vé/gói, hoàn tác điểm danh nhầm, xem lịch sử lên/xuống level và bật **Tham gia đánh giá** cho học viên khóa học.',
      },
      {
        img: 'b11_assessment',
        caption: 'Màn hình Đánh giá',
        label: 'Hình 10.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'paragraph',
        text: '**Các cột chính:** ID người dùng, **Vé** (mã QR), họ tên, **Cấp độ**, Sản phẩm/Loại/Chi tiết, ngày bắt đầu/kết thúc, **Loại vé** (COUNT/UNLIMITED), **Điểm danh hôm nay**, **Số buổi còn lại**, **Tham gia đánh giá**, Hành động.',
      },
    ],
    sections: [
      {
        id: 'c10-s1',
        title: '10.1 Điểm danh khách',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tìm khách/vé cần điểm danh bằng ô tìm kiếm (tên hoặc ID).',
              },
              {
                text: 'Nhấn biểu tượng ✓ **điểm danh** trên dòng → hộp xác nhận hiển thị → nhấn xác nhận.',
                figure: {
                  img: 'b11_checkin_confirm',
                  caption: 'Xác nhận điểm danh',
                  label: 'Hình 10.2',
                  width: 474,
                  height: 222,
                },
                compact: true,
              },
              {
                text: 'Khi thành công: số lần điểm danh hôm nay tăng; với gói tính theo buổi, **số buổi còn lại giảm 1**.',
              },
            ],
          },
          {
            type: 'table',
            headers: ['Thông báo', 'Nguyên nhân'],
            rows: [
              ['out of valid range', 'Hôm nay chưa đến ngày bắt đầu của gói'],
              [
                'Number of entries not available',
                'Gói tính theo buổi đã hết buổi',
              ],
            ],
          },
        ],
      },
      {
        id: 'c10-s2',
        title: '10.2 Hoàn tác điểm danh nhầm',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng **Check in history** (lịch sử điểm danh) trên dòng.',
                figure: {
                  img: 'b11_checkin_history',
                  caption: 'Lịch sử điểm danh',
                  label: 'Hình 10.3',
                  width: 900,
                  height: 555,
                },
              },
              {
                text: 'Tìm lần điểm danh nhầm → nhấn **Undo** → xác nhận. Với gói tính theo buổi, số buổi được cộng lại 1.',
              },
            ],
          },
        ],
      },
      {
        id: 'c10-s3',
        title: '10.3 Lịch sử lên / xuống level',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng **Promotion history** trên dòng để xem lịch sử thay đổi level của học viên (chỉ xem).',
                figure: {
                  img: 'b11_promotion_history',
                  caption: 'Lịch sử lên/xuống level',
                  label: 'Hình 10.4',
                  width: 900,
                  height: 1254,
                },
              },
            ],
          },
        ],
      },
      {
        id: 'c10-s4',
        title: '10.4 Cho phép học viên tham gia đánh giá',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Lọc **Có thể tham gia đánh giá** để xem các học viên khóa học đủ điều kiện.',
                figure: {
                  img: 'b11_assessment_join',
                  caption: 'Lọc học viên có thể tham gia đánh giá',
                  label: 'Hình 10.5',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Bật công tắc **Tham gia đánh giá** trên dòng học viên. Học viên sẽ xuất hiện trong công cụ đánh giá của huấn luyện viên (Chương 24).',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c11',
    no: 11,
    audience: 'admin',
    title: 'Lớp học',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Lớp học. **Mục đích:** quản lý toàn bộ lịch lớp. Mỗi **lớp** là **một buổi học** cụ thể có ngày, giờ, chương trình, level, huấn luyện viên và học viên.',
      },
      {
        img: 'b12_class_list',
        caption: 'Màn hình Quản lý lớp học (chế độ Hiển thị tất cả)',
        label: 'Hình 11.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'table',
        headers: ['Thành phần', 'Mô tả'],
        rows: [
          [
            'HIỂN THỊ TẤT CẢ / NHÓM THEO TUẦN / XEM LỊCH',
            '3 chế độ xem: dạng bảng, gom theo tuần, hoặc lịch tuần/tháng/bảng lớp',
          ],
          [
            'Thẻ số liệu tuần này',
            'Lớp mới, Học viên mới, Ghi danh mới, Tổng học viên',
          ],
          [
            'Bộ lọc',
            'Tên/ID lớp, loại dịch vụ, trạng thái, sức chứa, cấp độ, khung giờ (sáng/chiều/tối), huấn luyện viên, ngày học',
          ],
          [
            'Bộ lọc nâng cao',
            'Khoảng thời gian (Hôm nay / Tuần này / Tuần sau / Tháng này) và thứ trong tuần',
          ],
          ['Hành động trên dòng', 'Quản lý lớp · Chỉnh sửa lớp · Xóa lớp'],
        ],
      },
      {
        img: 'b12_class_adv_filter',
        caption: 'Bộ lọc nâng cao',
        label: 'Hình 11.2',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c11-s1',
        title: '11.1 Thêm một lớp',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM MỘT LỚP**. Hộp **Create New Class** hiển thị.',
                figure: {
                  img: 'b12_add_single',
                  caption: 'Tạo một lớp',
                  label: 'Hình 11.3',
                  width: 1350,
                  height: 981,
                },
                compact: true,
              },
              {
                text: 'Chọn **Class Type** (chương trình), **Class Level**, **Class Date**, **Start Time / End Time**; nhập **Maximum Students** (sĩ số tối đa — hệ thống tự gợi ý theo level) và Note.',
              },
              {
                text: 'Nhấn **CREATE CLASS**. Tên lớp được tự tạo theo mẫu `[Chương trình Level] - Thứ HH:MM-HH:MM`.',
              },
            ],
          },
        ],
      },
      {
        id: 'c11-s2',
        title: '11.2 Thêm nhanh nhiều lớp',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM NHIỀU LỚP**. Hộp **Thêm nhanh lớp học** hiển thị.',
                figure: {
                  img: 'b12_quick_add',
                  caption: 'Thêm nhanh nhiều lớp',
                  label: 'Hình 11.4',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Trong **Cấu hình lớp học**: chọn tuần (**Week start date**), **Loại lớp**, tick **các ngày trong tuần** cần mở lớp và số lớp mỗi ngày.',
              },
              {
                text: 'Hệ thống sinh danh sách lớp — chỉnh từng lớp: tên, level, ngày, giờ bắt đầu, giờ kết thúc, ghi chú.',
              },
              {
                text: 'Nhấn **TẠO N LỚP**. Nếu có lỗi (thiếu tên, giờ kết thúc trước giờ bắt đầu…), hệ thống liệt kê từng lớp bị lỗi để sửa.',
              },
            ],
          },
          {
            type: 'table',
            headers: ['Chương trình / Level', 'Sĩ số gợi ý'],
            rows: [
              ['SOF — Starfish, Seahorse', '4'],
              ['SOF — Seal', '6'],
              ['SOF — Barracuda, Swordfish, Dolphin', '8'],
              ['Adult Courses', '4'],
              ['Flying Fish', '24'],
              ['Khác', '4'],
            ],
          },
        ],
      },
      {
        id: 'c11-s3',
        title: '11.3 Sửa lớp và đổi trạng thái',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn ✏️ **Chỉnh sửa lớp** trên dòng lớp.',
                figure: {
                  img: 'b12_edit_class',
                  caption: 'Chỉnh sửa lớp',
                  label: 'Hình 11.5',
                  width: 1350,
                  height: 1158,
                },
                compact: true,
              },
              {
                text: 'Sửa tên, ngày, giờ, level, sĩ số, ghi chú và **Status**: **Active** (đang hoạt động), **Closed** (đã đóng), **Cancelled** (đã hủy).',
              },
              {
                text: 'Nhấn lưu để cập nhật.',
              },
            ],
          },
        ],
      },
      {
        id: 'c11-s4',
        title: '11.4 Xóa lớp',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn 🗑 **Xóa lớp** → đọc hộp "Xóa lớp học?" → nhấn **Xác nhận**.',
                figure: {
                  img: 'b12_delete_confirm',
                  caption: 'Xác nhận xóa lớp',
                  label: 'Hình 11.6',
                  width: 900,
                  height: 222,
                },
                compact: true,
              },
            ],
          },
          {
            type: 'note',
            kind: 'warn',
            text: 'Xóa lớp không đưa học viên của lớp trở lại danh sách **Học viên chờ xếp lớp**. Nếu cần xếp học viên sang lớp khác, hãy gỡ học viên khỏi lớp (Remove) trước khi xóa.',
          },
        ],
      },
      {
        id: 'c11-s5',
        title:
          '11.5 Quản lý thành viên lớp (học viên, huấn luyện viên, điểm danh)',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng **Quản lý lớp** trên dòng lớp. Hộp **Manage Class Members** hiển thị.',
                sub: [
                  '**Check-in Summary**: tổng học viên và số đã điểm danh hôm nay.',
                  '**Students List**: tìm theo tên/mã học viên, lọc level, lọc đã/chưa điểm danh hôm nay.',
                  '**Coaches List**: danh sách huấn luyện viên của lớp.',
                ],
                figure: {
                  img: 'b12_manage_dialog',
                  caption: 'Quản lý thành viên lớp',
                  label: 'Hình 11.7',
                  width: 1600,
                  height: 1101,
                },
              },
              {
                text: 'Thêm học viên: nhấn **ADD STUDENT**, chọn học viên có gói phù hợp rồi xác nhận.',
                figure: {
                  img: 'b12_manage_add_student',
                  caption: 'Thêm học viên vào lớp',
                  label: 'Hình 11.8',
                  width: 1350,
                  height: 258,
                },
              },
              {
                text: 'Gán huấn luyện viên: nhấn **ADD COACH**, chọn huấn luyện viên rồi xác nhận. Huấn luyện viên được gán sẽ thấy lớp này trong màn hình của mình.',
                figure: {
                  img: 'b12_manage_add_coach',
                  caption: 'Gán huấn luyện viên cho lớp',
                  label: 'Hình 11.9',
                  width: 900,
                  height: 258,
                },
              },
              {
                text: 'Điểm danh: nhấn biểu tượng ✓ **Check in** trên dòng học viên → **Confirm Checkin**; hoặc tick nhiều học viên → nhấn **CHECK IN SELECTED** (điểm danh ngay các học viên đã chọn, không hỏi lại). Xem/hoàn tác bằng biểu tượng lịch sử điểm danh trên dòng học viên.',
              },
              {
                text: 'Gỡ học viên hoặc huấn luyện viên khỏi lớp bằng biểu tượng **remove**.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'tip',
            text: 'Nên xếp học viên vào lớp qua **Học viên chờ xếp lớp** (Chương 13) — màn hình này kiểm tra đầy đủ level, hạn gói, sĩ số và việc học viên đã có lớp khác.',
          },
        ],
      },
      {
        id: 'c11-s6',
        title: '11.6 Xem theo tuần và sao chép lịch tuần',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **NHÓM THEO TUẦN**. Lớp được gom theo tuần (Thứ Hai → Chủ Nhật), có các nút xuất báo cáo tuần, sao chép tuần, xóa cả tuần.',
                figure: {
                  img: 'b12_group_week',
                  caption: 'Chế độ Nhóm theo tuần',
                  label: 'Hình 11.10',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn biểu tượng **Sao chép lớp học của tuần** trên tuần nguồn. Hộp sao chép hiển thị.',
                figure: {
                  img: 'b12_clone_week',
                  caption: 'Sao chép lịch lớp sang tuần khác',
                  label: 'Hình 11.11',
                  width: 1350,
                  height: 1215,
                },
              },
              {
                text: 'Chọn **Target week start** (tuần đích); chọn **Clone all classes from the source week** (tất cả) hoặc **Select specific classes to clone** (chọn từng lớp).',
              },
              {
                text: 'Tùy chọn: **Clone coach assignments** (sao chép huấn luyện viên), **Clone student enrollments** (sao chép học viên).',
              },
              {
                text: 'Xác nhận. Hệ thống báo số lớp/huấn luyện viên/học viên đã sao chép, các lớp bị trùng (đã có lớp cùng tên, ngày, giờ) và học viên bị bỏ qua kèm lý do (ví dụ gói đã hết hạn).',
              },
            ],
          },
          {
            type: 'note',
            kind: 'warn',
            text: 'Nút **Xóa tất cả lớp trong tuần** xóa **toàn bộ** lớp từ Thứ Hai đến Chủ Nhật của tuần đó — kể cả lớp đang bị ẩn bởi bộ lọc. Hãy bỏ bộ lọc và kiểm tra kỹ trước khi xóa.',
          },
        ],
      },
      {
        id: 'c11-s7',
        title: '11.7 Xem lịch và bảng lớp in được',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **XEM LỊCH**. Lịch hiển thị các lớp theo tuần.',
                figure: {
                  img: 'b12_cal_week',
                  caption: 'Lịch lớp theo tuần',
                  label: 'Hình 11.12',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Chuyển sang chế độ **tháng** để xem tổng quan cả tháng.',
                figure: {
                  img: 'b12_cal_month',
                  caption: 'Lịch lớp theo tháng',
                  label: 'Hình 11.13',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Chuyển sang chế độ **Sheet** để xem bảng lớp SOF / FF theo ngày (giờ, tên học viên, tuổi, level) — có thể in để dán tại hồ bơi.',
                figure: {
                  img: 'b12_cal_sheet',
                  caption: 'Bảng lớp theo ngày (Sheet)',
                  label: 'Hình 11.14',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn vào một lớp trên lịch để mở thông tin lớp (có thể chỉnh sửa trực tiếp).',
                figure: {
                  img: 'b12_cal_event',
                  caption: 'Mở thông tin lớp từ lịch',
                  label: 'Hình 11.15',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn biểu tượng **Xuất báo cáo tuần** để tải file Excel lịch tuần (ô được tô màu theo level).',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c12',
    no: 12,
    audience: 'admin',
    title: 'Dữ liệu gốc (danh bạ học viên)',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Dữ liệu gốc. **Mục đích:** danh bạ học viên theo chương trình — mỗi học viên một dòng (theo đăng ký gần nhất); quản lý level và lưu trữ học viên.',
      },
      {
        img: 'b13_master_sof',
        caption: 'Dữ liệu gốc — tab Khóa học trẻ em (School of Fish)',
        label: 'Hình 12.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'bullets',
        items: [
          '**Các tab:** Khóa học trẻ em (SOF) · Khóa học thi đấu (Flying Fish) · Khóa học người lớn · Khóa học riêng · Membership · Combo 10 · Lưu trữ.',
          'Tìm theo tên, email, số điện thoại; lọc **Cấp độ**, **Khóa học** (tab khóa học) hoặc **Vé**, **Gói** (tab vé). Biểu tượng ⬇ để tải Excel.',
          'Gói đã hết hạn được tô màu để dễ nhận biết.',
        ],
      },
      {
        img: 'b13_master_ff',
        caption: 'Dữ liệu gốc — tab Khóa học thi đấu (Flying Fish)',
        label: 'Hình 12.2',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c12-s1',
        title: '12.1 Xem thông tin khóa học của học viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn vào ô **Khóa học** trên dòng học viên. Hộp **Thông tin khóa học** hiển thị danh sách gói, ngày bắt đầu, ngày hết hạn và biểu đồ thời gian các gói.',
                figure: {
                  img: 'b13_course_info',
                  caption: 'Thông tin khóa học của học viên',
                  label: 'Hình 12.3',
                  width: 1600,
                  height: 1000,
                },
              },
            ],
          },
        ],
      },
      {
        id: 'c12-s2',
        title: '12.2 Đổi level học viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn vào ô **Cấp độ** trên dòng học viên.',
                figure: {
                  img: 'b13_edit_level',
                  caption: 'Đổi level học viên',
                  label: 'Hình 12.4',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Chọn level mới. Nếu chọn level **thấp hơn** hiện tại, hệ thống cảnh báo trước khi lưu.',
              },
              {
                text: 'Lưu. Thay đổi được ghi vào lịch sử level (người đổi, thời gian).',
              },
            ],
          },
        ],
      },
      {
        id: 'c12-s3',
        title: '12.3 Lưu trữ và bỏ lưu trữ học viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng **Lưu trữ học viên** trên dòng → xác nhận. Học viên chuyển sang tab **Lưu trữ**.',
                figure: {
                  img: 'b13_archive_confirm',
                  caption: 'Xác nhận lưu trữ học viên',
                  label: 'Hình 12.5',
                  width: 624,
                  height: 222,
                },
                compact: true,
              },
              {
                text: 'Để khôi phục: mở tab **Lưu trữ**, nhấn **Bỏ lưu trữ học viên** → xác nhận. Học viên quay về tab chương trình.',
                figure: {
                  img: 'b13_master_archive',
                  caption: 'Tab Lưu trữ',
                  label: 'Hình 12.6',
                  width: 1600,
                  height: 1091,
                },
              },
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Lưu trữ áp dụng **theo từng chương trình**: lưu trữ học viên ở School of Fish không ảnh hưởng tới các chương trình khác của học viên đó.',
          },
        ],
      },
    ],
  },
  {
    id: 'c13',
    no: 13,
    audience: 'admin',
    title: 'Học viên chờ xếp lớp',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Học viên chờ xếp lớp (dùng cho cả Quản trị viên và Huấn luyện viên). **Mục đích:** danh sách các **gói khóa học còn hiệu lực nhưng chưa được xếp vào lớp**; chọn lớp phù hợp còn chỗ và thêm học viên.',
      },
      {
        img: 'b14_pending',
        caption: 'Màn hình Học viên chờ xếp lớp (Pending Enrollment)',
        label: 'Hình 13.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'bullets',
        items: [
          'Bộ lọc: **Student ID / name** (nhấn Enter), **Service type** (SOF / Flying Fish / Adult Courses / Private Lesson), **Package**, **From date / To date**.',
          'Cột **Remaining**: số buổi còn lại hoặc *Unlimited*.',
        ],
      },
    ],
    sections: [
      {
        id: 'c13-s1',
        title: '13.1 Xếp lớp cho học viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tìm học viên trong danh sách → nhấn **Choose class**.',
              },
              {
                text: 'Hộp **Available classes** liệt kê các lớp phù hợp: lớp, ngày/giờ, level, huấn luyện viên, sức chứa (đã có / tối đa, còn n chỗ). Nhấn **Add** trên lớp muốn xếp.',
                figure: {
                  img: 'b14_available_classes',
                  caption: 'Danh sách lớp phù hợp',
                  label: 'Hình 13.2',
                  width: 1600,
                  height: 800,
                },
              },
              {
                text: 'Nhấn **Confirm add student** để xác nhận. Học viên biến khỏi danh sách chờ.',
                figure: {
                  img: 'b14_add_confirm',
                  caption: 'Xác nhận thêm học viên vào lớp',
                  label: 'Hình 13.3',
                  width: 666,
                  height: 258,
                },
                compact: true,
              },
            ],
          },
          {
            type: 'paragraph',
            text: '**Lớp được đề xuất phải:** đang Active; cùng chương trình với gói; ngày học từ hôm nay trở đi; còn chỗ; level tương thích (cùng level, hoặc lớp/học viên chưa đặt level). Với huấn luyện viên: chỉ hiện các lớp mình được giao.',
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Xếp lớp **không trừ buổi** — buổi chỉ bị trừ khi điểm danh. Nếu học viên bị gỡ khỏi lớp, gói sẽ quay lại danh sách chờ xếp lớp.',
          },
        ],
      },
    ],
  },
  {
    id: 'c14',
    no: 14,
    audience: 'admin',
    title: 'Báo cáo',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Báo cáo. **Mục đích:** xem thống kê doanh thu, khách hàng, học thử và xuất báo cáo Excel.',
      },
    ],
    sections: [
      {
        id: 'c14-s1',
        title: '14.1 Thẻ số liệu tổng quan',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Chọn kỳ: **Hàng ngày**, **Hàng tuần** hoặc **Hàng tháng**.',
                figure: {
                  img: 'b15_report_top',
                  caption: 'Báo cáo — thẻ số liệu tổng quan',
                  label: 'Hình 14.1',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Đọc các thẻ: Tổng doanh thu hôm nay / tuần này / tháng này, Khách hàng mới tháng này, Khách hàng đã lưu trữ.',
              },
            ],
          },
        ],
      },
      {
        id: 'c14-s2',
        title: '14.2 Danh sách đăng ký',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tại mục **Danh sách đăng ký**, chọn **Ngày thanh toán từ** và **Ngày thanh toán đến**.',
                figure: {
                  img: 'b15_report_registration',
                  caption: 'Báo cáo danh sách đăng ký',
                  label: 'Hình 14.2',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Xem **Tổng doanh thu**, **Doanh thu theo phương thức thanh toán**, số đăng ký đã/chưa xuất hóa đơn.',
              },
              {
                text: 'Nhấn **Tải báo cáo** để xuất Excel.',
              },
            ],
          },
        ],
      },
      {
        id: 'c14-s3',
        title: '14.3 Báo cáo học thử',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tại mục **Báo cáo học thử**, lọc theo tên/số điện thoại/email, chương trình, kết quả, ngày bắt đầu/kết thúc.',
                figure: {
                  img: 'b15_report_trial',
                  caption: 'Báo cáo học thử và tỷ lệ đăng ký',
                  label: 'Hình 14.3',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Xem tổng số buổi học thử, tỷ lệ **% đã đăng ký** và biểu đồ tròn **Tỷ lệ đăng ký từ học thử**.',
              },
              {
                text: 'Nhấn biểu tượng tải để xuất Excel.',
              },
            ],
          },
        ],
      },
      {
        id: 'c14-s4',
        title: '14.4 Doanh thu theo loại dịch vụ',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tại mục **Tổng doanh thu theo loại dịch vụ**, chọn tháng.',
                figure: {
                  img: 'b15_report_revenue',
                  caption: 'Biểu đồ doanh thu theo loại dịch vụ',
                  label: 'Hình 14.4',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn **Tải doanh thu** để xuất Excel.',
              },
            ],
          },
        ],
      },
      {
        id: 'c14-s5',
        title: '14.5 Danh sách khách hàng',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tại mục **Danh sách khách hàng** (khách có ít nhất một đăng ký), tìm theo ID, tên, email.',
                figure: {
                  img: 'b15_report_customer',
                  caption: 'Báo cáo danh sách khách hàng',
                  label: 'Hình 14.5',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn **Tải báo cáo** để xuất Excel.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Doanh thu mỗi đăng ký = giá (+ giá trẻ em với gói nhóm) − giảm giá (không âm). Để xem học viên đã lưu trữ, dùng tab **Lưu trữ** ở **Dữ liệu gốc**.',
          },
        ],
      },
    ],
  },
  {
    id: 'c15',
    no: 15,
    audience: 'admin',
    title: 'Phản hồi khảo sát',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Phản hồi khảo sát. **Mục đích:** xem tất cả phản hồi khách hàng gửi qua biểu mẫu online, xuất Excel và **chuyển phản hồi thành lịch học thử hoặc đăng ký**.',
      },
      {
        img: 'b16_responses',
        caption: 'Màn hình Phản hồi khảo sát khách hàng',
        label: 'Hình 15.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'bullets',
        items: [
          'Tìm theo tên, số điện thoại, email hoặc phiên bản; lọc **Biểu mẫu** và **Phiên bản**; nhấn **Tải xuống** để xuất Excel.',
          'Cột **Chuyển đổi**: "Đặt lịch học thử · Xong / —" và "Đăng ký · Xong / —" cho biết phản hồi đã được chuyển đổi hay chưa.',
        ],
      },
    ],
    sections: [
      {
        id: 'c15-s1',
        title: '15.1 Xem chi tiết phản hồi',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng 👁 trên dòng phản hồi. Hộp **Thông tin đã gửi** hiển thị các câu trả lời theo từng phần.',
                figure: {
                  img: 'b16_response_detail',
                  caption: 'Chi tiết phản hồi khảo sát',
                  label: 'Hình 15.2',
                  width: 1350,
                  height: 1254,
                },
              },
              {
                text: 'Dùng **Phản hồi trước / Phản hồi tiếp theo** để xem các phản hồi khác.',
              },
            ],
          },
        ],
      },
      {
        id: 'c15-s2',
        title: '15.2 Chuyển phản hồi thành lịch học thử hoặc đăng ký',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Cuộn xuống mục **Convert response**. Hệ thống tự tìm khách theo số điện thoại (hoặc tạo khách mới nếu đủ tên và ngày sinh) và xác định gói từ biểu mẫu.',
                figure: {
                  img: 'b16_response_convert',
                  caption: 'Mục chuyển đổi phản hồi',
                  label: 'Hình 15.3',
                  width: 1350,
                  height: 1254,
                },
              },
              {
                text: 'Nhấn **CONVERT TO TRIAL BOOKING** (chỉ với khóa học), nhập **Trial date**, Note → xác nhận. Lịch học thử mới có trạng thái Đang chờ.',
                figure: {
                  img: 'b16_convert_trial',
                  caption: 'Chuyển phản hồi thành lịch học thử',
                  label: 'Hình 15.4',
                  width: 900,
                  height: 477,
                },
                compact: true,
              },
              {
                text: 'Hoặc nhấn **CONVERT TO REGISTRATION**, nhập ngày bắt đầu, số lượng, giảm giá, phương thức thanh toán, ghi chú → xác nhận.',
                figure: {
                  img: 'b16_convert_registration',
                  caption: 'Chuyển phản hồi thành đăng ký',
                  label: 'Hình 15.5',
                  width: 900,
                  height: 693,
                },
                compact: true,
              },
            ],
          },
          {
            type: 'table',
            headers: ['Trạng thái', 'Ý nghĩa / cần làm'],
            rows: [
              ['Sẵn sàng (Ready)', 'Có thể chuyển đổi'],
              [
                'Thiếu số điện thoại / ngày sinh / tên',
                'Liên hệ khách để bổ sung, nhập tay',
              ],
              [
                'Chưa gắn gói',
                'Biểu mẫu chưa gắn gói dịch vụ — cấu hình tại Cài đặt biểu mẫu → ADD CUSTOM TYPE (Chương 18)',
              ],
              ['Cần chọn gói', 'Biểu mẫu gắn nhiều gói — chọn gói phù hợp'],
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Mỗi loại chuyển đổi chỉ thực hiện **một lần** cho mỗi phản hồi; sau đó hiển thị "Xong". Mở chi tiết phản hồi có thể khiến hệ thống tự liên kết hoặc tạo hồ sơ khách — kiểm tra trùng tại **Khách hàng → NHÓM** nếu cần.',
          },
        ],
      },
    ],
  },
  {
    id: 'c16',
    no: 16,
    audience: 'admin',
    title: 'Cài đặt dịch vụ',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Cài đặt dịch vụ. **Mục đích:** quản lý danh mục sản phẩm/dịch vụ 3 cấp dùng khi bán gói và xuất hóa đơn: **Sản phẩm** (Service) → **Loại dịch vụ** (Service Type) → **Gói** (Package).',
      },
      {
        img: 'c17_service_list',
        caption: 'Màn hình Cài đặt dịch vụ',
        label: 'Hình 16.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c16-s1',
        title: '16.1 Xem cấu trúc danh mục',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn vào sản phẩm (vd. **COURSE**) để mở các loại dịch vụ, rồi nhấn vào loại dịch vụ (vd. **SOF**) để xem các gói.',
                figure: {
                  img: 'c17_service_tree',
                  caption: 'Cây danh mục: Sản phẩm → Loại dịch vụ → Gói',
                  label: 'Hình 16.2',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Dùng các nút **THÊM DỊCH VỤ**, **ADD SERVICE TYPE**, **ADD PACKAGE** để thêm mới ở từng cấp; biểu tượng ✏️ / 🗑 để sửa / xóa.',
              },
            ],
          },
        ],
      },
      {
        id: 'c16-s2',
        title: '16.2 Thêm sản phẩm',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM DỊCH VỤ**, nhập tên sản phẩm và lưu.',
                figure: {
                  img: 'c17_add_service',
                  caption: 'Thêm sản phẩm',
                  label: 'Hình 16.3',
                  width: 900,
                  height: 276,
                },
                compact: true,
              },
            ],
          },
        ],
      },
      {
        id: 'c16-s3',
        title: '16.3 Cấu hình gói',
        blocks: [
          {
            type: 'paragraph',
            text: 'Nhấn ✏️ trên một gói (hoặc **ADD PACKAGE** để tạo mới). Hộp cấu hình gói có 3 tab:',
          },
          {
            type: 'steps',
            items: [
              {
                text: 'Tab **SERVICE INFORMATION** — thông tin gói. Nhập các trường rồi chuyển tab hoặc nhấn **CONFIRM**.',
                sub: [
                  '**Package Name**: tên gói khi bán; **Invoice Name**: tên dòng hàng trên hóa đơn.',
                  '**Service For**: CHILD / ADULT / GROUP / BUSINESS; **Price** (đã gồm VAT), **Price for Child** (gói nhóm); **VAT Rate (%)**; **Unit Measure**.',
                  '**Duration (days)**: thời hạn gói; **Visit Type**: count (theo buổi) / unlimited; **Visit Balance**: số buổi của gói.',
                ],
                figure: {
                  img: 'c17_pkg_tab1',
                  caption: 'Tab thông tin gói',
                  label: 'Hình 16.4',
                  width: 1600,
                  height: 592,
                },
              },
              {
                text: 'Tab **CONFIRMATION CONTENT** — nội dung phiếu xác nhận đăng ký: điều khoản (tiếng Việt / tiếng Anh) và thông tin hỗ trợ.',
                figure: {
                  img: 'c17_pkg_tab2',
                  caption: 'Tab nội dung phiếu xác nhận',
                  label: 'Hình 16.5',
                  width: 1600,
                  height: 1157,
                },
              },
              {
                text: 'Tab **FORM CONFIGURATION** — bộ câu hỏi riêng của gói, dùng khi thêm gói vào biểu mẫu khảo sát (ADD CUSTOM TYPE, Chương 18). Nhấn **ADD FIELD** để thêm câu hỏi, **ADD SECTION** để thêm phần.',
                figure: {
                  img: 'c17_pkg_tab3',
                  caption: 'Tab cấu hình biểu mẫu của gói',
                  label: 'Hình 16.6',
                  width: 1600,
                  height: 877,
                },
              },
              {
                text: 'Nhấn **CONFIRM** để lưu gói.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'warn',
            text: 'Thay đổi giá / VAT / mẫu phiếu chỉ áp dụng cho các đăng ký bán **sau** thời điểm thay đổi. Xóa một cấp sẽ ẩn cả các cấp con bên dưới — hãy cân nhắc trước khi xóa.',
          },
        ],
      },
    ],
  },
  {
    id: 'c17',
    no: 17,
    audience: 'admin',
    title: 'Cài đặt khóa học',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Cài đặt khóa học. **Mục đích:** khai báo **level** và **stage** (kỹ năng) cho từng chương trình khóa học — nền tảng cho đánh giá và xếp lớp.',
      },
      {
        img: 'c18_course_list',
        caption: 'Màn hình Cài đặt khóa học',
        label: 'Hình 17.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'steps',
        items: [
          {
            text: 'Nhấn vào chương trình (vd. **SOF**) để xem danh sách level.',
            figure: {
              img: 'c18_course_sof',
              caption: 'Danh sách level của chương trình SOF',
              label: 'Hình 17.2',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Nhấn vào một level (vd. **Starfish**) để xem các stage (kỹ năng) của level đó. Dùng ✏️ / 🗑 để sửa / xóa level hoặc stage.',
            figure: {
              img: 'c18_course_level_open',
              caption: 'Các stage trong một level',
              label: 'Hình 17.3',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Để thêm level: nhấn **ADD LEVEL**, nhập **số level** và **tên level** (không trùng số level trong cùng chương trình) rồi lưu.',
            figure: {
              img: 'c18_add_level',
              caption: 'Thêm level',
              label: 'Hình 17.4',
              width: 900,
              height: 366,
            },
            compact: true,
          },
          {
            text: 'Để thêm stage: trong level, nhấn thêm stage, nhập **tên stage** và **Video URL** (video hướng dẫn kỹ năng, không bắt buộc) rồi lưu.',
          },
        ],
      },
      {
        type: 'note',
        kind: 'note',
        text: 'Ô tìm kiếm trên màn hình này hiện chưa hoạt động — hãy mở từng chương trình để tìm level/stage.',
      },
    ],
    sections: [],
  },
  {
    id: 'c18',
    no: 18,
    audience: 'admin',
    title: 'Cài đặt biểu mẫu khảo sát',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Cài đặt biểu mẫu. **Mục đích:** tạo và quản lý các biểu mẫu khảo sát khách hàng (mỗi biểu mẫu có link và mã QR riêng) cùng các **phiên bản** của biểu mẫu.',
      },
      {
        img: 'c19_templates',
        caption:
          'Màn hình Mẫu khảo sát khách hàng: danh sách biểu mẫu (trái) và phiên bản (phải)',
        label: 'Hình 18.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c18-s1',
        title: '18.1 Tạo biểu mẫu mới',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **BIỂU MẪU MỚI**, nhập tên biểu mẫu (**Form name**) và nhấn **CREATE**.',
                figure: {
                  img: 'c19_new_form',
                  caption: 'Tạo biểu mẫu mới',
                  label: 'Hình 18.2',
                  width: 666,
                  height: 282,
                },
                compact: true,
              },
              {
                text: 'Chọn biểu mẫu vừa tạo ở cột trái, nhấn **THÊM PHIÊN BẢN BIỂU MẪU** để tạo phiên bản đầu tiên (trạng thái **Bản nháp**).',
              },
            ],
          },
        ],
      },
      {
        id: 'c18-s2',
        title: '18.2 Soạn nội dung phiên bản',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Trình soạn thảo mở ở tab **EDIT FORM VERSION**. Nhập **Tên mẫu**, **Form title** (tiêu đề hiển thị cho khách) và **Form description**.',
                figure: {
                  img: 'c19_editor',
                  caption: 'Trình soạn thảo phiên bản biểu mẫu',
                  label: 'Hình 18.3',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Mỗi **SECTION** (phần) có tiêu đề, mô tả và danh sách câu hỏi. Mỗi câu hỏi có nhãn (**Question / field label**), **Field type** và ô **Required** (bắt buộc). Dùng mũi tên để đổi thứ tự, biểu tượng nhân bản/xóa để sao chép/xóa câu hỏi.',
              },
              {
                text: 'Nhấn **ADD FIELD** để thêm câu hỏi và chọn loại câu hỏi.',
                sub: [
                  'Short text / Long text; Phone number; Email; Date; Number; Radio choice / Checkboxes / Dropdown; Service (khách chọn dịch vụ); Consent checkbox (đồng ý điều khoản).',
                ],
                figure: {
                  img: 's19_field_types',
                  caption: 'Các loại câu hỏi',
                  label: 'Hình 18.4',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Nhấn **ADD CUSTOM TYPE** để nhập bộ câu hỏi đã cấu hình sẵn của một gói (Chương 16, tab Form configuration) **và gắn gói đó với biểu mẫu** — cần thiết để chuyển phản hồi thành đăng ký/học thử.',
                figure: {
                  img: 's19_custom_type_picker',
                  caption: 'Chọn gói để thêm bộ câu hỏi (Custom type)',
                  label: 'Hình 18.5',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Chuyển sang tab **PREVIEW** để xem biểu mẫu như khách nhìn thấy.',
                figure: {
                  img: 'c19_preview',
                  caption: 'Xem trước biểu mẫu',
                  label: 'Hình 18.6',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nhấn **SAVE CHANGES** để lưu bản nháp.',
              },
            ],
          },
        ],
      },
      {
        id: 'c18-s3',
        title: '18.3 Kích hoạt phiên bản',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Mở phiên bản ở trạng thái Bản nháp, nhấn **ACTIVATE VERSION** (hoặc biểu tượng kích hoạt trên dòng phiên bản) và xác nhận.',
              },
              {
                text: 'Điều kiện: có ít nhất **1 phần**, **1 câu hỏi** và **1 ô Consent bắt buộc**.',
              },
              {
                text: '**Chỉ một phiên bản Đang hoạt động** tại một thời điểm — kích hoạt bản mới sẽ chuyển bản đang hoạt động về Bản nháp.',
              },
            ],
          },
        ],
      },
      {
        id: 'c18-s4',
        title: '18.4 Các thao tác khác trên phiên bản',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng ⋮ **Thêm hành động** trên dòng phiên bản.',
                figure: {
                  img: 'c19_more_actions',
                  caption: 'Menu thao tác của phiên bản',
                  label: 'Hình 18.7',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Chọn: **Xem phản hồi** (phản hồi của phiên bản này) · **Nhân bản phiên bản** (tạo bản nháp mới) · **Copy form link** (sao chép đường dẫn gửi khách) · **Open QR page** (mở trang mã QR để in/trình chiếu) · **Xóa phiên bản** (không xóa được bản đang hoạt động).',
              },
              {
                text: 'Ví dụ: **Xem phản hồi** mở danh sách phản hồi của phiên bản, có panel chuyển đổi như Chương 15.',
                figure: {
                  img: 'c19_version_responses',
                  caption: 'Phản hồi của một phiên bản',
                  label: 'Hình 18.8',
                  width: 1600,
                  height: 1091,
                },
              },
            ],
          },
          {
            type: 'note',
            kind: 'tip',
            text: 'Để chỉnh sửa an toàn biểu mẫu đang sử dụng: **Nhân bản phiên bản** → chỉnh sửa bản nháp → **PREVIEW** → **ACTIVATE VERSION**. Chia sẻ biểu mẫu bằng **Copy form link** hoặc **Open QR page**.',
          },
        ],
      },
    ],
  },
  {
    id: 'c19',
    no: 19,
    audience: 'admin',
    title: 'Nhập và xuất dữ liệu Excel',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Nhập dữ liệu. **Mục đích:** tải xuống hoặc nhập hàng loạt dữ liệu bằng Excel. Mỗi lần nhập đều được **xem trước**, **ghi lịch sử** và **có thể hoàn tác**.',
      },
      {
        img: 'c20_import',
        caption: 'Màn hình Nhập dữ liệu',
        label: 'Hình 19.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
      {
        type: 'table',
        headers: ['Tab', 'Khi nhập file'],
        rows: [
          [
            'Khách hàng',
            'Tạo mới hoặc cập nhật (khớp theo số điện thoại hoặc email)',
          ],
          [
            'Huấn luyện viên',
            'Tạo/cập nhật theo email (tài khoản mới có mật khẩu mặc định)',
          ],
          ['Đăng ký thử', 'Chỉ tạo mới'],
          ['Đăng ký', 'Chỉ tạo mới; tự tìm/tạo khách và danh mục dịch vụ'],
          ['Lớp học', 'Chỉ tạo mới'],
          ['Dữ liệu gốc', 'Cập nhật level học viên'],
          ['Danh mục dịch vụ', 'Tạo/cập nhật theo tên gói'],
          [
            'Báo cáo',
            'Chỉ tải xuống: báo cáo đăng ký, học thử, khách hàng, doanh thu theo tháng',
          ],
        ],
      },
    ],
    sections: [
      {
        id: 'c19-s1',
        title: '19.1 Nhập dữ liệu từ file Excel',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Chọn tab loại dữ liệu cần nhập (vd. **KHÁCH HÀNG**).',
              },
              {
                text: 'Trong khối **Tải xuống**, nhấn **TẢI MẪU TRỐNG** để tải file mẫu (hoặc **TẢI XUỐNG DỮ LIỆU** để lấy dữ liệu hiện có).',
              },
              {
                text: 'Điền dữ liệu vào file mẫu.',
              },
              {
                text: 'Trong khối **Tải lên**, nhấn **CHỌN TỆP** (hoặc kéo-thả file .xlsx).',
              },
              {
                text: 'Nhấn **XEM TRƯỚC** — chưa có gì được lưu. Hệ thống hiển thị số dòng sẽ tạo mới/cập nhật, hoặc danh sách dòng lỗi "Dòng X: …".',
                figure: {
                  img: 'c20_import_preview',
                  caption: 'Xem trước kết quả nhập dữ liệu',
                  label: 'Hình 19.2',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Nếu có dòng lỗi: sửa file rồi tải lại. Nếu hợp lệ: kiểm tra kỹ → nhấn **XÁC NHẬN NHẬP**.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Nguyên tắc **"tất cả hoặc không gì cả"**: chỉ cần một dòng lỗi thì toàn bộ file không được nhập.',
          },
        ],
      },
      {
        id: 'c19-s2',
        title: '19.2 Lịch sử và hoàn tác',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Khối **Lịch sử tải lên / tải xuống** liệt kê: Thời điểm, Tệp, Trạng thái (Đã áp dụng / Đã hoàn tác / Thất bại), số dòng (mới / cập nhật / lỗi), người thực hiện.',
              },
              {
                text: 'Nhấn **CHI TIẾT** để xem chi tiết từng dòng của một lần nhập.',
              },
              {
                text: 'Nhấn **HOÀN TÁC** để hủy toàn bộ thay đổi của lần nhập đó — dòng tạo mới bị gỡ, dòng cập nhật được khôi phục giá trị cũ.',
              },
            ],
          },
        ],
      },
      {
        id: 'c19-s3',
        title: '19.3 Tải báo cáo',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Mở tab **BÁO CÁO** và nhấn nút tương ứng: Tải báo cáo đăng ký / học thử / khách hàng / doanh thu (chọn tháng doanh thu).',
                figure: {
                  img: 'c20_import_report',
                  caption: 'Tab Báo cáo trong Nhập dữ liệu',
                  label: 'Hình 19.3',
                  width: 1600,
                  height: 1091,
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c20',
    no: 20,
    audience: 'admin',
    title: 'Nhật ký kiểm toán',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Nhật ký kiểm toán. **Mục đích:** tra cứu **ai đã làm gì, khi nào, kết quả ra sao** với mọi thao tác thay đổi dữ liệu. Màn hình chỉ để xem.',
      },
      {
        type: 'steps',
        items: [
          {
            text: 'Mở màn hình, dùng bộ lọc: tìm theo đường dẫn/tên/email người dùng; **Hành động** (Đăng nhập, Tạo mới, Cập nhật, Xóa, Gộp, Nhập dữ liệu, Hoàn tác…); **Phân hệ** (Khách hàng, Đăng ký, Lớp học, Điểm danh…); **Kết quả** (Thành công / Thất bại). Nhấn **Áp dụng**.',
            figure: {
              img: 'c21_audit',
              caption: 'Màn hình Nhật ký kiểm toán',
              label: 'Hình 20.1',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Nhấn **CHI TIẾT** trên dòng để xem IP, route và dữ liệu gửi lên (thông tin nhạy cảm như mật khẩu đã được che).',
            figure: {
              img: 'c21_audit_detail',
              caption: 'Chi tiết một bản ghi kiểm toán',
              label: 'Hình 20.2',
              width: 1350,
              height: 513,
            },
          },
        ],
      },
      {
        type: 'note',
        kind: 'tip',
        text: 'Để xem lịch sử đăng nhập, lọc **Phân hệ = Xác thực**.',
      },
    ],
    sections: [],
  },
  {
    id: 'c21',
    no: 21,
    audience: 'admin',
    title: 'Quản lý tài khoản',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Quản lý tài khoản. **Mục đích:** tạo và quản lý tài khoản đăng nhập của nhân viên (Quản trị viên và Huấn luyện viên).',
      },
      {
        img: 'c22_account',
        caption: 'Màn hình Quản lý tài khoản',
        label: 'Hình 21.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c21-s1',
        title: '21.1 Thêm tài khoản',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM TÀI KHOẢN**.',
                figure: {
                  img: 'c22_account_add',
                  caption: 'Thêm tài khoản nhân viên',
                  label: 'Hình 21.2',
                  width: 666,
                  height: 639,
                },
                compact: true,
              },
              {
                text: 'Nhập **Tên**, **Email (tên đăng nhập)**, **Mật khẩu** (8–16 ký tự), chọn **Vai trò** (Quản trị viên / Huấn luyện viên).',
              },
              {
                text: 'Nhấn **Tạo**.',
              },
            ],
          },
        ],
      },
      {
        id: 'c21-s2',
        title: '21.2 Đặt lại mật khẩu',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng 🔑 **Đặt lại mật khẩu** trên dòng tài khoản.',
                figure: {
                  img: 'c22_account_reset',
                  caption: 'Đặt lại mật khẩu',
                  label: 'Hình 21.3',
                  width: 666,
                  height: 363,
                },
                compact: true,
              },
              {
                text: 'Nhập **Mật khẩu mới** → nhấn **Đặt lại**. Thông báo mật khẩu mới cho nhân viên.',
              },
            ],
          },
        ],
      },
      {
        id: 'c21-s3',
        title: '21.3 Vô hiệu hóa / kích hoạt lại tài khoản',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn biểu tượng **Vô hiệu hóa** trên dòng → xác nhận. Nhân viên sẽ không đăng nhập được nữa.',
                figure: {
                  img: 'c22_account_deactivate',
                  caption: 'Xác nhận vô hiệu hóa tài khoản',
                  label: 'Hình 21.4',
                  width: 900,
                  height: 258,
                },
                compact: true,
              },
              {
                text: 'Để mở lại, nhấn **Kích hoạt lại** trên dòng tài khoản đã vô hiệu hóa.',
              },
            ],
          },
          {
            type: 'bullets',
            items: [
              'Không thể tự vô hiệu hóa hoặc tự hạ quyền tài khoản của chính mình (tài khoản của bạn có nhãn **(bạn)**).',
              'Hệ thống luôn phải còn **ít nhất một Quản trị viên** đang hoạt động.',
              'Email không được trùng với tài khoản khác đang hoạt động.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c22',
    no: 22,
    audience: 'admin',
    title: 'Cài đặt tra cứu phụ huynh',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Cài đặt tra cứu phụ huynh. **Mục đích:** chia sẻ mã QR trang tra cứu cho phụ huynh, bật/tắt xác minh bảo mật và quản lý poster quảng cáo trên trang tra cứu.',
      },
      {
        img: 'c23_pl_setting',
        caption: 'Màn hình Cài đặt tra cứu phụ huynh',
        label: 'Hình 22.1',
        type: 'figure',
        width: 1600,
        height: 1091,
      },
    ],
    sections: [
      {
        id: 'c22-s1',
        title: '22.1 Chia sẻ mã QR tra cứu',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tại thẻ **Mã QR tra cứu phụ huynh**, chọn **Sao chép liên kết**, **Tải mã QR** (ảnh) hoặc **In mã QR**.',
              },
              {
                text: 'Đặt mã QR tại quầy hoặc gửi liên kết cho phụ huynh.',
              },
            ],
          },
        ],
      },
      {
        id: 'c22-s2',
        title: '22.2 Bật / tắt xác minh bảo mật',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tại thẻ **Xác minh bảo mật**, bật công tắc để **BẬT — bắt buộc Cloudflare thành công** (phụ huynh phải qua bước xác minh trước khi tra cứu), hoặc tắt để **TẮT — bỏ qua Cloudflare**.',
              },
              {
                text: 'Thay đổi có hiệu lực ngay.',
              },
            ],
          },
        ],
      },
      {
        id: 'c22-s3',
        title: '22.3 Quản lý poster',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhấn **THÊM POSTER**, nhập **Tên poster** (tối đa 160 ký tự), nhấn **Chọn hình ảnh** (tối đa 10MB) và **Tải hình ảnh lên**.',
                figure: {
                  img: 'c23_add_poster',
                  caption: 'Thêm poster',
                  label: 'Hình 22.2',
                  width: 900,
                  height: 414,
                },
                compact: true,
              },
              {
                text: 'Danh sách poster hiển thị tên, ảnh xem trước, trạng thái và công tắc hiển thị.',
                figure: {
                  img: 'c23_pl_posters',
                  caption: 'Danh sách poster trên trang tra cứu',
                  label: 'Hình 22.3',
                  width: 1600,
                  height: 1091,
                },
              },
              {
                text: 'Poster mới ở trạng thái **Đang ẩn**. Bật công tắc ở cột **Hiển thị** để hiển thị trên trang tra cứu — chỉ **một poster** được hiển thị tại một thời điểm.',
              },
              {
                text: 'Dùng **Chỉnh sửa** để đổi tên/thay ảnh, **Xóa** để xóa poster.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c23',
    no: 23,
    audience: 'admin',
    title: 'Tra cứu phụ huynh dành cho nhân viên',
    blocks: [
      {
        type: 'paragraph',
        text: '**Menu:** Cài đặt → Tra cứu phụ huynh quản trị. **Mục đích:** nhân viên tra cứu gói của học viên khi phụ huynh gọi điện/đến quầy (không cần xác minh, không giới hạn số lần) và **gia hạn gói / điều chỉnh số buổi còn lại**.',
      },
      {
        type: 'steps',
        items: [
          {
            text: 'Chọn **Student code** (mã học viên) hoặc **Phone** (số điện thoại).',
            figure: {
              img: 'c24_pl_admin_empty',
              caption: 'Màn hình Tra cứu phụ huynh — Admin',
              label: 'Hình 23.1',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Nhập giá trị → nhấn **LOOKUP**. Thông tin học viên và các gói (trạng thái, thời hạn, số buổi còn lại, lớp, huấn luyện viên, lịch học) hiển thị.',
            figure: {
              img: 'c24_pl_admin_result',
              caption: 'Kết quả tra cứu của nhân viên',
              label: 'Hình 23.2',
              width: 1600,
              height: 1091,
            },
          },
          {
            text: 'Để điều chỉnh, nhấn **EDIT** trên gói cần sửa. Hộp **Edit package** hiển thị.',
            sub: [
              '**New end date**: ngày kết thúc mới (không trước ngày bắt đầu).',
              '**Remaining sessions**: số buổi còn lại (chỉ với gói theo buổi, ≥ 0).',
            ],
            figure: {
              img: 'c24_edit_package',
              caption: 'Điều chỉnh gói',
              label: 'Hình 23.3',
              width: 900,
              height: 390,
            },
            compact: true,
          },
          {
            text: 'Lưu. Thông tin được cập nhật ngay; phụ huynh thấy số liệu mới khi tra cứu.',
          },
        ],
      },
      {
        type: 'note',
        kind: 'note',
        text: 'Mỗi lần tra cứu và điều chỉnh đều được ghi vào Nhật ký kiểm toán (kèm giá trị trước/sau khi sửa). Chức năng này chỉ đổi ngày kết thúc và số buổi còn lại; không đổi gói, khách hàng hay lịch sử điểm danh.',
      },
    ],
    sections: [],
  },
  {
    id: 'c24',
    no: 24,
    audience: 'coach',
    title: 'Huấn luyện viên: Đánh giá level học viên',
    blocks: [
      {
        type: 'paragraph',
        text: 'Huấn luyện viên đăng nhập bằng **Email** và **Mật khẩu** do Quản trị viên cấp; sau khi đăng nhập, hệ thống mở màn hình **Assessment**. Quên mật khẩu: liên hệ Quản trị viên để được đặt lại.',
      },
      {
        type: 'paragraph',
        text: 'Menu của huấn luyện viên gồm: **Assessment**, **Class Management**, **Master Data**, **Pending Enrollment** và **User Guide**. Huấn luyện viên chỉ thao tác được với **học viên trong các lớp mình được giao**.',
      },
      {
        type: 'steps',
        items: [
          {
            text: 'Mở **Assessment**. Chọn thẻ chương trình **SCHOOL OF FISH** hoặc **FLYING FISH**.',
            figure: {
              img: 'd25_assess_home',
              caption: 'Công cụ đánh giá — chọn chương trình',
              label: 'Hình 24.1',
              width: 1600,
              height: 1059,
            },
          },
          {
            text: 'Danh sách học viên hiển thị: mã, họ tên, tuổi, **Last update** (tháng cập nhật level gần nhất), level, thông tin liên hệ, chương trình. Có thể tìm, lọc level, **Export** Excel.',
            figure: {
              img: 'd25_sof_list',
              caption: 'Danh sách học viên cần đánh giá',
              label: 'Hình 24.2',
              width: 1600,
              height: 1059,
            },
          },
          {
            text: 'Nhấn **View / edit** trên dòng học viên để mở hộp **Manage User Level**.',
            sub: [
              'Mỗi level có các **stage** kèm công tắc: **bật** = đã hoàn thành stage; **tắt** (có xác nhận) = bỏ đánh dấu.',
              '**Quick Set Level**: chọn nhanh level → xác nhận; hệ thống tự đánh dấu hoàn thành mọi stage ở các level thấp hơn.',
            ],
            figure: {
              img: 'd25_manage_level_dialog',
              caption: 'Đánh giá level học viên',
              label: 'Hình 24.3',
              width: 1600,
              height: 1059,
            },
          },
          {
            text: 'Xem mục **History** để biết ngày, giờ, huấn luyện viên và hành động (hoàn thành stage, lên level, xuống level).',
            figure: {
              img: 'd25_level_history',
              caption: 'Lịch sử đánh giá',
              label: 'Hình 24.4',
              width: 1600,
              height: 1059,
            },
          },
        ],
      },
      {
        type: 'note',
        kind: 'note',
        text: '**Cách tính level:** Level hiện tại = level cao nhất đã hoàn thành TẤT CẢ stage + 1. Ví dụ: hoàn thành toàn bộ stage của level 1 và 2 → level hiện tại là 3; khi bật stage cuối cùng của level 3, học viên tự động lên level 4.',
      },
      {
        type: 'paragraph',
        text: '**Học viên xuất hiện trong danh sách khi:** có gói khóa học của chương trình, còn hạn, còn buổi; đã được Quản trị viên bật **Tham gia đánh giá**; và đang học trong lớp huấn luyện viên được giao.',
      },
    ],
    sections: [],
  },
  {
    id: 'c25',
    no: 25,
    audience: 'coach',
    title: 'Huấn luyện viên: Lớp được giao và điểm danh',
    blocks: [
      {
        type: 'steps',
        items: [
          {
            text: 'Mở **Class Management**. Bảng **Assigned Classes** liệt kê lớp được giao: tên lớp, ngày, giờ, loại, level, huấn luyện viên, trạng thái, sĩ số, điểm danh hôm nay (x/y).',
            figure: {
              img: 'd26_coach_class',
              caption: 'Danh sách lớp được giao',
              label: 'Hình 25.1',
              width: 1600,
              height: 1059,
            },
          },
          {
            text: 'Nhấn vào một lớp để mở chi tiết: **Check in Information**, danh sách học viên (họ tên, ngày sinh, level, gói, **Sessions Remaining**, đã điểm danh hôm nay chưa). Gói hết hạn được tô màu cảnh báo.',
            figure: {
              img: 'd26_class_dialog_crop',
              caption: 'Chi tiết lớp và danh sách học viên',
              label: 'Hình 25.2',
              width: 1600,
              height: 817,
            },
          },
          {
            text: 'Tick chọn các học viên có mặt → nhấn **Check in selected**. Hệ thống điểm danh ngay các học viên đã chọn (không hỏi lại). Để điểm danh từng học viên, nhấn biểu tượng **Check in** trên dòng → **Confirm Checkin**.',
            figure: {
              img: 'd26_select_students',
              caption: 'Chọn học viên để điểm danh',
              label: 'Hình 25.3',
              width: 1600,
              height: 817,
            },
          },
          {
            text: 'Kết quả: học viên đã điểm danh hiện nhãn **Checked In**, số đã điểm danh trong **Check-in Summary** tăng lên; số buổi còn lại giảm 1 (gói theo buổi).',
            figure: {
              img: 'd26_checkin_done',
              caption: 'Kết quả sau khi điểm danh',
              label: 'Hình 25.4',
              width: 1600,
              height: 817,
            },
          },
          {
            text: 'Nếu điểm danh nhầm: nhấn **View check-in history** trên dòng học viên → **Undo check-in** → **Confirm Undo Checkin**.',
            figure: {
              img: 'd26_checkin_history',
              caption: 'Lịch sử điểm danh và hoàn tác',
              label: 'Hình 25.5',
              width: 900,
              height: 516,
            },
          },
          {
            text: 'Nhấn **VIEW CALENDAR** để xem lịch dạy của bạn theo tuần/tháng.',
            figure: {
              img: 'd26_coach_calendar',
              caption: 'Lịch lớp của huấn luyện viên',
              label: 'Hình 25.6',
              width: 1600,
              height: 1059,
            },
          },
        ],
      },
      {
        type: 'bullets',
        items: [
          'Không điểm danh trước **ngày bắt đầu** của gói; **mỗi gói tối đa 1 lần/ngày**; gói theo buổi phải **còn buổi**.',
          'Chỉ điểm danh cho lớp diễn ra hôm nay. Trên điện thoại, danh sách học viên hiển thị dạng thẻ cho dễ thao tác.',
        ],
      },
    ],
    sections: [],
  },
  {
    id: 'c26',
    no: 26,
    audience: 'coach',
    title: 'Huấn luyện viên: Danh bạ học viên và xếp lớp',
    blocks: [],
    sections: [
      {
        id: 'c26-s1',
        title: '26.1 Master Data (chỉ xem)',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Mở **Master Data** để xem danh bạ học viên theo chương trình: tìm, lọc, tải Excel, xem thông tin gói.',
                figure: {
                  img: 'd27_coach_master',
                  caption: 'Danh bạ học viên (huấn luyện viên)',
                  label: 'Hình 26.1',
                  width: 1600,
                  height: 1059,
                },
              },
            ],
          },
          {
            type: 'table',
            headers: ['Quyền', 'Quản trị viên', 'Huấn luyện viên'],
            rows: [
              ['Xem, tìm, lọc, tải Excel', '✔', '✔'],
              ['Xem/sửa hồ sơ, lưu trữ/bỏ lưu trữ', '✔', '✘'],
              ['Sửa level bằng ô Cấp độ', '✔', '✘ (chỉ hiển thị)'],
            ],
          },
        ],
      },
      {
        id: 'c26-s2',
        title: '26.2 Pending Enrollment (xếp lớp)',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Mở **Pending Enrollment**. Danh sách gồm các gói khóa học còn hiệu lực nhưng **chưa được xếp vào lớp nào**. Có thể lọc theo **Student ID / name**, **Service type**, **Package**, **From date / To date**.',
                figure: {
                  img: 'd27_coach_pending',
                  caption: 'Học viên chờ xếp lớp (huấn luyện viên)',
                  label: 'Hình 26.2',
                  width: 1600,
                  height: 1059,
                },
              },
              {
                text: 'Tìm học viên cần xếp lớp → nhấn **Choose class**.',
              },
              {
                text: 'Hộp **Available classes** liệt kê các lớp phù hợp: lớp, ngày/giờ, level, huấn luyện viên, sức chứa (đã có / tối đa, còn n chỗ). Danh sách **chỉ gồm các lớp bạn được giao**, cùng chương trình, từ hôm nay trở đi, còn chỗ và cùng level.',
              },
              {
                text: 'Nhấn **Add** trên lớp muốn xếp → **Confirm add student**. Học viên biến khỏi danh sách chờ.',
              },
            ],
          },
          {
            type: 'note',
            kind: 'note',
            text: 'Xếp lớp **không trừ buổi** — buổi chỉ bị trừ khi điểm danh. Nếu không có lớp phù hợp, liên hệ Quản trị viên để mở lớp hoặc tăng sĩ số.',
          },
        ],
      },
    ],
  },
  {
    id: 'c27',
    no: 27,
    audience: 'public',
    title: 'Khách hàng: Điền biểu mẫu khảo sát qua mã QR',
    blocks: [
      {
        type: 'paragraph',
        text: 'Các trang trong chương này **không cần đăng nhập** và được tối ưu cho điện thoại. Nhân viên nên nắm để hướng dẫn khách.',
      },
    ],
    sections: [
      {
        id: 'c27-s1',
        title: '27.1 Trang mã QR tại quầy',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Nhân viên mở trang mã QR từ **Cài đặt biểu mẫu → Open QR page** và đặt tại quầy/kiosk.',
                figure: {
                  img: 'e28_qr',
                  caption: 'Trang mã QR khảo sát',
                  label: 'Hình 27.1',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Trang có các nút **Open form**, **Copy link**, **Download QR** (tải ảnh), **Print QR** và phần hướng dẫn "How it works".',
              },
            ],
          },
        ],
      },
      {
        id: 'c27-s2',
        title: '27.2 Khách điền biểu mẫu',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Khách dùng camera điện thoại quét mã QR (hoặc mở link được gửi). Biểu mẫu hiển thị.',
                figure: {
                  img: 'e28_form_mobile',
                  caption: 'Biểu mẫu khảo sát trên điện thoại',
                  label: 'Hình 27.2',
                  width: 780,
                  height: 1688,
                },
                compact: true,
              },
              {
                text: 'Khách điền các câu hỏi; câu hỏi bắt buộc có dấu *. Một số câu hỏi chỉ hiện khi khách chọn đáp án liên quan.',
                figure: {
                  img: 'e28_form_desktop',
                  caption: 'Điền thông tin vào biểu mẫu',
                  label: 'Hình 27.3',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Ở phần **Confirmation** cuối biểu mẫu, khách đọc và tick ô **đồng ý điều khoản** (có liên kết tới chính sách bảo vệ dữ liệu cá nhân).',
                figure: {
                  img: 'e28_form_confirmation',
                  caption: 'Phần xác nhận đồng ý và nút gửi',
                  label: 'Hình 27.4',
                  width: 1600,
                  height: 900,
                },
              },
              {
                text: 'Khách nhấn **SUBMIT INFORMATION**. Màn hình cảm ơn hiển thị; khách có thể nhấn **SUBMIT ANOTHER RESPONSE** để gửi thêm (vd. đăng ký cho con thứ hai).',
                figure: {
                  img: 'e28_thanks',
                  caption: 'Gửi biểu mẫu thành công',
                  label: 'Hình 27.5',
                  width: 1600,
                  height: 507,
                },
              },
            ],
          },
          {
            type: 'table',
            headers: ['Tình huống', 'Hướng dẫn khách'],
            rows: [
              [
                'This form is currently unavailable',
                'Biểu mẫu chưa có phiên bản đang hoạt động — báo nhân viên; khách nhấn Try again sau',
              ],
              [
                'Lỗi tại ô nhập',
                'Kiểm tra email đúng định dạng, số điện thoại 8–15 chữ số, đã điền đủ câu bắt buộc',
              ],
              [
                'Báo đã nhận phản hồi',
                'Biểu mẫu đã được gửi trước đó — không cần gửi lại',
              ],
              ['Gửi quá nhiều lần', 'Chờ khoảng 1 phút rồi thử lại'],
            ],
          },
        ],
      },
      {
        id: 'c27-s3',
        title: '27.3 Chính sách bảo vệ dữ liệu cá nhân',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Khách nhấn liên kết **Personal Data Protection Policy** để đọc chính sách: thông tin thu thập, mục đích, sự đồng ý, chia sẻ, lưu trữ, quyền của khách, bảo mật, liên hệ.',
                figure: {
                  img: 'e28_privacy',
                  caption: 'Trang chính sách bảo vệ dữ liệu cá nhân',
                  label: 'Hình 27.6',
                  width: 1600,
                  height: 900,
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c28',
    no: 28,
    audience: 'public',
    title: 'Phụ huynh: Tra cứu thông tin học viên',
    blocks: [
      {
        type: 'paragraph',
        text: '**Mục đích:** phụ huynh tự tra cứu gói đăng ký, lớp học, giáo viên, level, số buổi còn lại và lịch học của con. **Cách mở:** quét mã QR do trung tâm cung cấp (dán tại quầy hoặc gửi qua tin nhắn).',
      },
      {
        type: 'steps',
        items: [
          {
            text: 'Mở trang **Tra cứu thông tin lớp học**. Tại **Tra cứu bằng**, chọn **Mã học viên** hoặc **Số điện thoại đã đăng ký**.',
            figure: {
              img: 'e29_lookup',
              caption: 'Trang tra cứu thông tin lớp học',
              label: 'Hình 28.1',
              width: 1600,
              height: 900,
            },
          },
          {
            text: 'Nhập mã học viên (số) hoặc số điện thoại đã đăng ký tại trung tâm.',
            figure: {
              img: 'e29_lookup_phone',
              caption: 'Tra cứu bằng số điện thoại',
              label: 'Hình 28.2',
              width: 1600,
              height: 900,
            },
          },
          {
            text: 'Nếu có mục **Xác minh bảo mật**, hoàn thành bước xác minh.',
          },
          {
            text: 'Nhấn **TRA CỨU**. Mỗi học viên hiển thị trên một thẻ (một số điện thoại có thể ra nhiều học viên); dịch vụ chia theo tab loại dịch vụ (vd. **Khóa học**).',
            figure: {
              img: 'e29_lookup_result',
              caption: 'Kết quả tra cứu',
              label: 'Hình 28.3',
              width: 1600,
              height: 900,
            },
          },
          {
            text: 'Cuộn xuống để xem từng gói: trạng thái, level, thời hạn, số buổi còn lại và mục **Lớp học và lịch học** (tên lớp, giáo viên, ngày giờ học).',
            figure: {
              img: 'e29_lookup_classes',
              caption: 'Thông tin gói, lớp học và lịch học',
              label: 'Hình 28.4',
              width: 1600,
              height: 900,
            },
          },
        ],
      },
      {
        img: 'e29_lookup_mobile',
        caption: 'Kết quả tra cứu trên điện thoại',
        label: 'Hình 28.5',
        type: 'figure',
        width: 780,
        height: 1688,
      },
      {
        type: 'table',
        headers: ['Thông tin', 'Ý nghĩa'],
        rows: [
          ['Trạng thái', 'Đang hoạt động / Đã hết buổi / Đã hết hạn'],
          ['Level', 'Level hiện tại của học viên'],
          ['Thời hạn gói', 'Ngày bắt đầu – ngày kết thúc'],
          ['Số buổi còn lại', 'Số buổi chưa sử dụng, hoặc "Không giới hạn"'],
          [
            'Lớp học và lịch học',
            'Tên lớp, giáo viên, ngày giờ học, level, trạng thái lớp. Nếu gói chưa có lớp: "Gói này chưa được liên kết với lớp học"',
          ],
        ],
      },
    ],
    sections: [
      {
        id: 'c28-s1',
        title: '28.1 Thông báo thường gặp',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: '"Không tìm thấy thông tin học viên phù hợp": kiểm tra lại mã học viên hoặc số điện thoại đã đăng ký.',
                figure: {
                  img: 'e29_lookup_notfound',
                  caption: 'Không tìm thấy học viên',
                  label: 'Hình 28.6',
                  width: 1600,
                  height: 900,
                },
              },
            ],
          },
          {
            type: 'table',
            headers: ['Thông báo', 'Cách xử lý'],
            rows: [
              [
                'Bạn đã tra cứu quá nhiều lần…',
                'Mỗi số điện thoại/mã học viên chỉ được tra cứu 5 lần mỗi phút. Chờ hết thời gian đếm ngược',
              ],
              [
                'Vui lòng hoàn tất mã CAPTCHA / CAPTCHA đã hết hạn',
                'Hoàn thành hoặc làm lại bước xác minh bảo mật',
              ],
              [
                'Không thể thực hiện tra cứu',
                'Lỗi tạm thời — thử lại sau; nếu vẫn lỗi, liên hệ trung tâm',
              ],
            ],
          },
          {
            type: 'note',
            kind: 'tip',
            text: 'Khi phụ huynh gặp khó khăn, hãy liên hệ quầy lễ tân — nhân viên trung tâm có thể tra cứu thay trên hệ thống quản trị.',
          },
        ],
      },
    ],
  },
  {
    id: 'c29',
    no: 29,
    audience: 'reference',
    title: 'Các quy trình làm việc thường gặp',
    blocks: [],
    sections: [
      {
        id: 'c29-s1',
        title: '29.1 Hành trình tổng thể của một học viên',
        blocks: [
          {
            type: 'table',
            headers: ['Giai đoạn', 'Màn hình', 'Kết quả'],
            rows: [
              [
                '1. Khách quan tâm',
                'Biểu mẫu khảo sát QR / Trang chủ → Đặt lịch học thử',
                'Phản hồi khảo sát hoặc lịch học thử (Đang chờ)',
              ],
              [
                '2. Học thử',
                'Đặt lịch học thử → cập nhật kết quả',
                'Enroll → tạo hồ sơ khách',
              ],
              ['3. Bán gói', 'Trang chủ / Đăng ký → GHI DANH', 'Đăng ký mới'],
              [
                '4. Hóa đơn, phiếu xác nhận',
                'Đăng ký → Tạo hóa đơn / Tải xác nhận',
                'Hóa đơn M-Invoice, phiếu PDF',
              ],
              [
                '5. Xếp lớp',
                'Học viên chờ xếp lớp → Choose class',
                'Học viên vào lớp',
              ],
              [
                '6. Học và điểm danh',
                'Lớp học (Admin) / Class Management (HLV) / Đánh giá (quầy)',
                'Điểm danh, trừ buổi',
              ],
              [
                '7. Đánh giá level',
                'Assessment (HLV)',
                'Level cập nhật, có lịch sử',
              ],
              [
                '8. Phụ huynh theo dõi',
                'Trang tra cứu phụ huynh',
                'Phụ huynh xem gói, lớp, số buổi còn lại',
              ],
            ],
          },
        ],
      },
      {
        id: 'c29-s2',
        title: '29.2 Khách mới đến quầy đăng ký khóa học cho con',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: '**Trang chủ** → ô **Tìm khách hàng**: tìm theo số điện thoại phụ huynh.',
              },
              {
                text: 'Không thấy → **Khách hàng mới** → nhập họ tên, ngày sinh của **con** (hệ thống tự xếp loại Child).',
              },
              {
                text: 'Chọn gói khóa học (vd. COURSE → SOF → gói 3 tháng).',
              },
              {
                text: 'Nhập số điện thoại, địa chỉ, **Parent Name** → CONFIRM.',
              },
              {
                text: '**Confirm Registration**: chọn level, ngày bắt đầu, phương thức thanh toán, giảm giá → CONFIRM.',
              },
              {
                text: '**Đăng ký** → **Tạo hóa đơn** (nếu khách cần) → **Tải xác nhận đăng ký** → in/gửi PDF cho phụ huynh.',
              },
              {
                text: '**Học viên chờ xếp lớp** → tìm học viên → **Choose class** → chọn lớp phù hợp → **Add**.',
              },
              {
                text: '**Đánh giá** → bật **Tham gia đánh giá** cho đăng ký này.',
              },
              {
                text: 'Hướng dẫn phụ huynh quét QR tra cứu để tự theo dõi.',
              },
            ],
          },
        ],
      },
      {
        id: 'c29-s3',
        title: '29.3 Bán vé bơi lẻ cho khách vãng lai',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: '**Trang chủ** → **Khách hàng / tổ chức hiện có** → tìm mã **999999** (Ẩn danh).',
              },
              {
                text: 'Chọn gói vé → nhập **Walk-in Customer Name** (nếu có).',
              },
              {
                text: 'Xác nhận thanh toán.',
              },
              {
                text: '**Đánh giá** → tìm đăng ký → điểm danh.',
              },
              {
                text: 'Xuất hóa đơn "bán cho người tiêu dùng" tại **Đăng ký** nếu cần.',
              },
            ],
          },
        ],
      },
      {
        id: 'c29-s4',
        title: '29.4 Học thử → ghi danh',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Tạo lịch học thử (Trang chủ → Đặt lịch học thử, hoặc màn hình Đặt lịch học thử).',
              },
              {
                text: 'Sau buổi học: **Đặt lịch học thử** → ✏️ → cập nhật **Result**.',
              },
              {
                text: 'Khách đồng ý học: chọn **Enroll** → xác nhận (hồ sơ khách được tạo).',
              },
              {
                text: '**Khách hàng → NHÓM**: kiểm tra trùng hồ sơ, gộp nếu cần.',
              },
              {
                text: 'Bán gói: **Đăng ký → GHI DANH** → chọn khách → tiếp tục như quy trình 29.2 từ bước 3.',
              },
            ],
          },
        ],
      },
      {
        id: 'c29-s5',
        title: '29.5 Lên lịch lớp cho tuần mới',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: '**Lớp học → NHÓM THEO TUẦN** → tại tuần hiện tại nhấn **Sao chép lớp học của tuần**.',
              },
              {
                text: 'Chọn tuần đích, tick **Clone coach assignments** và **Clone student enrollments** → xác nhận.',
              },
              {
                text: 'Đọc kết quả: lớp trùng, học viên bị bỏ qua (thường do gói hết hạn).',
              },
              {
                text: 'Thêm lớp mới nếu cần; gán huấn luyện viên qua **Quản lý lớp → ADD COACH**.',
              },
              {
                text: 'Xếp học viên còn thiếu lớp qua **Học viên chờ xếp lớp**.',
              },
              {
                text: '**Xuất báo cáo tuần** hoặc **XEM LỊCH → Sheet** để in lịch dán tại hồ bơi.',
              },
            ],
          },
        ],
      },
      {
        id: 'c29-s6',
        title: '29.6 Một buổi dạy của huấn luyện viên',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: 'Đăng nhập → **Class Management** → lọc ngày hôm nay.',
              },
              {
                text: 'Mở lớp → tick học viên có mặt → **Check in selected**.',
              },
              {
                text: 'Nếu nhầm: **View check-in history** → **Undo check-in**.',
              },
              {
                text: 'Sau buổi học: **Assessment** → chọn chương trình → **View / edit** từng học viên → bật các stage học viên đã đạt.',
              },
            ],
          },
        ],
      },
      {
        id: 'c29-s7',
        title: '29.7 Phụ huynh báo số buổi hoặc hạn gói chưa đúng',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: '**Cài đặt → Tra cứu phụ huynh quản trị** → tra theo mã học viên hoặc số điện thoại.',
              },
              {
                text: 'Đối chiếu số buổi và lịch sử điểm danh (tại **Đánh giá → lịch sử điểm danh**).',
              },
              {
                text: 'Nếu cần điều chỉnh: **Edit package** → sửa **New end date** / **Remaining sessions** → lưu.',
              },
            ],
          },
        ],
      },
      {
        id: 'c29-s8',
        title: '29.8 Nhập dữ liệu hàng loạt từ Excel',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                text: '**Cài đặt → Nhập dữ liệu** → chọn tab dữ liệu.',
              },
              {
                text: '**TẢI MẪU TRỐNG** → điền dữ liệu.',
              },
              {
                text: '**CHỌN TỆP** → **XEM TRƯỚC** → sửa lỗi nếu có → **XÁC NHẬN NHẬP**.',
              },
              {
                text: 'Nhập sai? Vào **Lịch sử tải lên / tải xuống** → **HOÀN TÁC**.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c30',
    no: 30,
    audience: 'reference',
    title: 'Quy tắc nghiệp vụ cần biết',
    blocks: [],
    sections: [
      {
        id: 'c30-s1',
        title: '30.1 Trạng thái gói',
        blocks: [
          {
            type: 'table',
            headers: ['Trạng thái', 'Điều kiện'],
            rows: [
              [
                'Đang hoạt động',
                'Chưa qua ngày kết thúc và (với gói theo buổi) còn buổi',
              ],
              ['Đã hết buổi', 'Gói theo buổi, số buổi còn lại = 0'],
              ['Đã hết hạn', 'Đã qua ngày kết thúc'],
            ],
          },
          {
            type: 'bullets',
            items: [
              'Gói theo buổi đã hết hạn hiển thị 0 buổi còn lại với phụ huynh, trừ khi nhân viên cập nhật số buổi sau ngày hết hạn.',
              'Gói không giới hạn hiển thị "Không giới hạn".',
            ],
          },
        ],
      },
      {
        id: 'c30-s2',
        title: '30.2 Điểm danh',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Mỗi lần điểm danh trừ **1 buổi** (gói theo buổi); hoàn tác cộng lại 1 buổi.',
              'Không được điểm danh **trước ngày bắt đầu** gói. Gói đã hết hạn vẫn có thể điểm danh muộn.',
              'Huấn luyện viên: mỗi gói tối đa **1 lần/ngày**. **Xếp lớp không trừ buổi.**',
            ],
          },
        ],
      },
      {
        id: 'c30-s3',
        title: '30.3 Giá và hóa đơn',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Ngày kết thúc = ngày bắt đầu + thời hạn gói.',
              'Giá = đơn giá × số lượng (+ giá trẻ em × số lượng trẻ em với gói nhóm). Tổng chi phí = giá − giảm giá (không âm). Giá đã gồm VAT.',
              'Giá, VAT, tên hóa đơn, mẫu phiếu xác nhận được chốt tại thời điểm bán.',
              'Đăng ký đã xuất hóa đơn thì không sửa/xóa được.',
            ],
          },
        ],
      },
      {
        id: 'c30-s4',
        title: '30.4 Level và sĩ số lớp',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Level được khai báo theo chương trình tại **Cài đặt khóa học**; level học viên lưu theo từng đăng ký và thay đổi qua đánh giá (huấn luyện viên) hoặc Dữ liệu gốc (quản trị viên). Mọi thay đổi đều có lịch sử.',
              'Sĩ số tính theo số học viên không trùng lặp đang hoạt động trong lớp. Lớp đầy thì không thêm được học viên.',
            ],
          },
        ],
      },
      {
        id: 'c30-s5',
        title: '30.5 Khách hàng',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Dưới 16 tuổi → Child; từ 16 tuổi → Adult; tổ chức → Business.',
              'Khách vãng lai dùng mã 999999 (Ẩn danh); không in phiếu xác nhận cho khách vãng lai.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'c31',
    no: 31,
    audience: 'reference',
    title: 'Lưu ý khi sử dụng',
    blocks: [
      {
        type: 'table',
        headers: ['Tình huống', 'Cách làm đúng'],
        rows: [
          [
            'Xóa cả tuần lớp học',
            'Bỏ bộ lọc và kiểm tra toàn bộ lớp của tuần (Thứ Hai → Chủ Nhật) trước khi xóa',
          ],
          [
            'Đổi chương trình (Class Type) của lớp đã tạo',
            'Xóa lớp và tạo lại với chương trình đúng',
          ],
          [
            'Thêm học viên vào lớp',
            'Ưu tiên dùng Học viên chờ xếp lớp để hệ thống kiểm tra đầy đủ điều kiện',
          ],
          [
            'Sao chép tuần kèm học viên',
            'Rà lại sĩ số các lớp ở tuần đích sau khi sao chép',
          ],
          [
            'Xuất Excel từ chế độ Xem lịch',
            'Lịch tính tuần từ Chủ Nhật; Nhóm theo tuần tính từ Thứ Hai',
          ],
          [
            'Tạo lớp Flying Fish – Silver',
            'Kiểm tra và chỉnh Maximum Students cho phù hợp',
          ],
          [
            'Xóa lớp có học viên',
            'Gỡ học viên khỏi lớp trước nếu cần xếp sang lớp khác',
          ],
          ['Tạo lịch học thử', 'Nhập email hợp lệ'],
          [
            'Sau khi Enroll học thử',
            'Kiểm tra trùng hồ sơ, rồi bán gói tại Đăng ký → GHI DANH',
          ],
          [
            'Sửa hồ sơ khách doanh nghiệp',
            'Kiểm tra lại cột Loại khách hàng sau khi lưu',
          ],
          [
            'Nhập dữ liệu nhiều dòng',
            'Dùng Cài đặt → Nhập dữ liệu (có xem trước, lịch sử, hoàn tác)',
          ],
          ['Gửi phiếu xác nhận cho khách', 'Tải PDF và gửi thủ công'],
          [
            'Trước khi xuất hóa đơn',
            'Kiểm tra kỹ thông tin; điều chỉnh hóa đơn thực hiện trên M-Invoice',
          ],
          [
            'Sửa biểu mẫu đang hoạt động',
            'Nhân bản phiên bản → sửa bản nháp → xem trước → kích hoạt',
          ],
          [
            'Chia sẻ biểu mẫu khảo sát',
            'Dùng Copy form link hoặc Open QR page trong Cài đặt biểu mẫu',
          ],
          [
            'Phụ huynh bị giới hạn tra cứu',
            'Hướng dẫn chờ 1 phút; nhân viên dùng Tra cứu phụ huynh quản trị',
          ],
          [
            'Xóa dịch vụ, gói, level, stage',
            'Cân nhắc trước khi xóa; liên hệ bộ phận kỹ thuật nếu cần khôi phục',
          ],
          [
            'Cần chặn ngay một tài khoản',
            'Vô hiệu hóa tài khoản và báo bộ phận kỹ thuật',
          ],
        ],
      },
    ],
    sections: [],
  },
  {
    id: 'c32',
    no: 32,
    audience: 'reference',
    title: 'Câu hỏi thường gặp',
    blocks: [
      {
        type: 'table',
        headers: ['Câu hỏi', 'Trả lời'],
        rows: [
          [
            'Màn hình trắng hoặc không tải được dữ liệu?',
            'Tải lại trang (F5). Nếu vẫn lỗi, đăng xuất rồi đăng nhập lại; nếu còn tiếp diễn, chụp màn hình và báo bộ phận kỹ thuật.',
          ],
          [
            'Đang làm việc thì bị đưa về trang đăng nhập?',
            'Phiên làm việc đã hết hạn hoặc tài khoản đã đăng nhập ở thiết bị khác. Đăng nhập lại.',
          ],
          [
            'Không thấy một số menu?',
            'Menu hiển thị theo vai trò. Huấn luyện viên chỉ thấy 4 mục. Cần thêm quyền, liên hệ Quản trị viên.',
          ],
          [
            'Không sửa / xóa được một đăng ký?',
            'Đăng ký đã được xuất hóa đơn. Điều chỉnh hóa đơn trên M-Invoice.',
          ],
          [
            'Không in được phiếu xác nhận?',
            'Phiếu xác nhận không áp dụng cho khách vãng lai (999999). Hãy dùng hồ sơ khách thật.',
          ],
          [
            'Điểm danh báo "out of valid range"?',
            'Gói chưa đến ngày bắt đầu. Kiểm tra Start Date của đăng ký; sửa nếu nhập sai (khi chưa xuất hóa đơn).',
          ],
          [
            'Điểm danh báo "Number of entries not available"?',
            'Gói đã hết buổi. Tư vấn khách mua gói mới, hoặc điều chỉnh số buổi tại Tra cứu phụ huynh quản trị nếu có sai sót.',
          ],
          [
            'Huấn luyện viên không thấy học viên trong công cụ đánh giá?',
            'Kiểm tra: đã bật Tham gia đánh giá; gói còn hạn, còn buổi; học viên đã được xếp vào lớp huấn luyện viên được giao.',
          ],
          [
            'Huấn luyện viên không thấy lớp của mình?',
            'Quản trị viên cần gán huấn luyện viên vào lớp tại Lớp học → Quản lý lớp → ADD COACH.',
          ],
          [
            'Học viên không có trong danh sách chờ xếp lớp?',
            'Học viên đã được xếp lớp, hoặc gói đã hết hạn/hết buổi, hoặc gói không phải khóa học.',
          ],
          [
            'Không có lớp nào trong "Available classes"?',
            'Không có lớp đang hoạt động, cùng chương trình, từ hôm nay trở đi, còn chỗ và cùng level. Tạo lớp mới hoặc tăng sĩ số lớp hiện có.',
          ],
          [
            'Nhập Excel báo lỗi và không lưu gì?',
            'Đây là cơ chế an toàn — sửa các dòng lỗi được liệt kê rồi tải lại.',
          ],
          [
            'Nhập nhầm file Excel?',
            'Nhập dữ liệu → Lịch sử tải lên / tải xuống → HOÀN TÁC lần nhập đó.',
          ],
          [
            'Gộp nhầm hồ sơ khách?',
            'Khách hàng → Lịch sử gộp hồ sơ → Hoàn tác.',
          ],
          [
            'Biểu mẫu khảo sát báo "currently unavailable"?',
            'Biểu mẫu chưa có phiên bản đang hoạt động. Vào Cài đặt biểu mẫu → ACTIVATE VERSION.',
          ],
          [
            'Muốn biết ai đã sửa / xóa dữ liệu?',
            'Cài đặt → Nhật ký kiểm toán → lọc theo Phân hệ, Hành động, người dùng.',
          ],
        ],
      },
    ],
    sections: [],
  },
  {
    id: 'c33',
    no: 33,
    audience: 'reference',
    title: 'Bảng thuật ngữ',
    blocks: [
      {
        type: 'table',
        headers: ['Thuật ngữ', 'Tiếng Việt', 'Giải thích'],
        rows: [
          ['Admin', 'Quản trị viên', 'Nhân viên quầy / quản lý, có toàn quyền'],
          [
            'Coach',
            'Huấn luyện viên',
            'Người đứng lớp; thao tác giới hạn trong lớp được giao',
          ],
          ['User / Customer', 'Khách hàng', 'Người mua dịch vụ hoặc học viên'],
          [
            'Anonymous / Walk-in',
            'Khách vãng lai / Ẩn danh',
            'Khách mua lẻ, dùng mã 999999',
          ],
          [
            'Product / Service',
            'Sản phẩm / Dịch vụ',
            'Cấp 1 của danh mục: COURSE, TICKET',
          ],
          [
            'Service Type / Program',
            'Loại dịch vụ / Chương trình',
            'Cấp 2: SOF, Flying Fish, Adult Courses…',
          ],
          ['Package', 'Gói', 'Cấp 3: gói cụ thể có giá, thời hạn, số buổi'],
          ['Registration', 'Đăng ký', 'Một lần khách mua một gói'],
          [
            'Enroll',
            'Ghi danh',
            'Bán gói cho khách / chuyển khách học thử thành học viên',
          ],
          ['Trial Booking', 'Lịch học thử', 'Buổi học thử của khách tiềm năng'],
          [
            'Visit Type: count / unlimited',
            'Theo buổi / Không giới hạn',
            'Cách tính lượt của gói',
          ],
          [
            'Remaining sessions',
            'Số buổi còn lại',
            'Số buổi chưa sử dụng của gói theo buổi',
          ],
          [
            'Check in',
            'Điểm danh',
            'Ghi nhận học viên có mặt; trừ 1 buổi với gói theo buổi',
          ],
          ['Undo', 'Hoàn tác', 'Hủy một thao tác trước đó'],
          [
            'Class',
            'Lớp học',
            'Một buổi học cụ thể có ngày giờ, huấn luyện viên, học viên',
          ],
          ['Maximum Students', 'Sĩ số tối đa', 'Số học viên tối đa của lớp'],
          ['Clone week', 'Sao chép tuần', 'Sao chép lịch lớp sang tuần khác'],
          [
            'Level / Stage',
            'Cấp độ / Kỹ năng',
            'Level gồm nhiều stage cần hoàn thành',
          ],
          [
            'SOF (School of Fish)',
            'Khóa học trẻ em',
            'Chương trình bơi cho trẻ em',
          ],
          ['FF (Flying Fish)', 'Khóa học thi đấu', 'Chương trình bơi thi đấu'],
          [
            'Join Assessment',
            'Tham gia đánh giá',
            'Cho phép học viên xuất hiện trong công cụ đánh giá',
          ],
          [
            'Pending Enrollment',
            'Học viên chờ xếp lớp',
            'Gói khóa học còn hiệu lực nhưng chưa có lớp',
          ],
          [
            'Archive / Unarchive',
            'Lưu trữ / Bỏ lưu trữ',
            'Ẩn / hiện lại học viên trong một chương trình',
          ],
          ['Merge', 'Gộp hồ sơ', 'Hợp nhất các hồ sơ khách trùng'],
          [
            'M-Invoice',
            'Hóa đơn điện tử',
            'Hóa đơn xuất qua hệ thống M-Invoice',
          ],
          [
            'Registration confirmation',
            'Phiếu xác nhận đăng ký',
            'Phiếu PDF gửi khách sau khi mua gói',
          ],
          [
            'Form / Version',
            'Biểu mẫu / Phiên bản',
            'Biểu mẫu online thu thập thông tin khách',
          ],
          [
            'Draft / Active',
            'Bản nháp / Đang hoạt động',
            'Trạng thái phiên bản biểu mẫu',
          ],
          [
            'Consent',
            'Đồng ý điều khoản',
            'Ô khách tick đồng ý chính sách dữ liệu',
          ],
          [
            'Convert',
            'Chuyển đổi',
            'Biến phản hồi khảo sát thành lịch học thử / đăng ký',
          ],
          [
            'Parent Lookup',
            'Tra cứu phụ huynh',
            'Trang công khai để phụ huynh tra cứu thông tin con',
          ],
          [
            'CAPTCHA',
            'Xác minh bảo mật',
            'Bước xác minh chống tra cứu tự động',
          ],
          [
            'Import / Export',
            'Nhập / Xuất dữ liệu',
            'Đưa dữ liệu vào / ra hệ thống bằng Excel',
          ],
          [
            'Audit Log',
            'Nhật ký kiểm toán',
            'Lịch sử mọi thao tác thay đổi dữ liệu',
          ],
          [
            'Deactivate / Reactivate',
            'Vô hiệu hóa / Kích hoạt lại',
            'Khóa / mở lại tài khoản nhân viên',
          ],
        ],
      },
    ],
    sections: [],
  },
];
